import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Upload, Plus, Trash2, GripVertical, Eye, ChevronUp, ChevronDown, Type, RotateCcw } from "lucide-react";
import { FONT_GROUPS, fontCss } from "./CertificateDesigner";
import api, { BACKEND, mergeSaveCmsField } from "@/lib/api";
import { useCms } from "@/lib/cms";
import { Header } from "./Schools";
import { Logo } from "@/components/Brand";
import { AdminModal } from "@/components/AdminModal";
import { resolveTrainerImage, trainerImageStyle } from "@/lib/trainers";

const tabs = ["Site & Header", "Home + KPI Builder", "Programs Page", "About us", "Members", "Testimonials", "Contact page", "Homepage Typography"];

export default function Content() {
  const [searchParams] = useSearchParams();
  const initialTab = Math.min(Math.max(parseInt(searchParams.get("tab") || "0", 10), 0), tabs.length - 1);
  const [tab, setTab] = useState(isNaN(initialTab) ? 0 : initialTab);
  return (
    <div className="fade-up">
      <Header title="Website Content" subtitle="Edit your public website without touching code." actions={
        <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#0F172A] text-sm font-medium px-4 py-2 rounded-lg" data-testid="cms-preview">
          <Eye className="w-4 h-4" /> Preview site
        </a>
      } />
      <div className="mt-6 flex flex-wrap gap-1 border-b border-gray-200">
        {tabs.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} data-testid={`cms-tab-${i}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === i ? "border-[#4F46E5] text-[#4F46E5]" : "border-transparent text-gray-500 hover:text-gray-900"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === 0 && <SitePanel />}
        {tab === 1 && <HomePanel />}
        {tab === 2 && <ProgramsPanel />}
        {tab === 3 && <AboutPanel />}
        {tab === 4 && <MembersPanel />}
        {tab === 5 && <TestimonialsPanel />}
        {tab === 6 && <ContactPanel />}
        {tab === 7 && <TypographyPanel />}
      </div>
    </div>
  );
}

export function useEditable(endpoint) {
  const [data, setData] = useState(null);
  // Pristine snapshot from the initial fetch — lets callers compute exactly
  // which fields they changed, so saving doesn't clobber fields updated
  // elsewhere (e.g. the header/nav builders) between load and save.
  const baselineRef = useRef(null);
  useEffect(() => { api.get(endpoint).then((r) => { setData(r.data); baselineRef.current = r.data; }); }, [endpoint]);
  return [data, setData, baselineRef];
}

export async function uploadFile(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/cms/upload", fd);
  return data.url;
}

export const Field = ({ label, children }) => (
  <div className="grid gap-1.5">
    <label className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</label>
    {children}
  </div>
);
export const Input = ({ className = "", ...props }) => <input {...props} className={`px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white ${className}`} />;
export const TextArea = (props) => <textarea {...props} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white min-h-24" />;

const ALLOWED_IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "gif"];

export function ImageUpload({ value, onChange, testid }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_IMAGE_EXT.includes(ext)) {
      toast.error("Please upload a PNG, JPG, JPEG, WEBP, or GIF file.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try { const url = await uploadFile(f); onChange(url); toast.success(ext === "gif" ? "GIF uploaded" : "Image uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false); e.target.value = "";
  };
  const src = value ? (value.startsWith("http") ? value : `${BACKEND}${value}`) : "";
  return (
    <div className="flex items-center gap-3">
      {src && <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />}
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload…" className="flex-1 px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm" />
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handle} className="hidden" data-testid={testid} />
      <button onClick={() => ref.current.click()} disabled={uploading} className="inline-flex items-center gap-1 bg-white border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg">
        <Upload className="w-3.5 h-3.5" /> {uploading ? "…" : value ? "Replace" : "Upload"}
      </button>
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-sm text-gray-500 hover:text-rose-600 px-2 py-2" data-testid={testid ? `${testid}-remove` : undefined}>
          Remove
        </button>
      )}
    </div>
  );
}

function SitePanel() {
  const { reload } = useCms();
  const [d, setD, baselineRef] = useEditable("/cms/site");
  if (!d) return <div className="text-gray-500">Loading…</div>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const setSocial = (k, v) => setD({ ...d, social: { ...(d.social || {}), [k]: v } });
  // Merge only the fields this panel actually changed onto the latest live
  // document, instead of overwriting the whole record with this session's
  // possibly-stale copy — prevents an unrelated Website Content save from
  // resetting header/nav settings edited elsewhere.
  const save = async () => {
    const merged = await mergeSaveCmsField("/cms/site", d, baselineRef.current);
    setD(merged);
    baselineRef.current = merged;
    await reload();
    toast.success("Site saved");
  };
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company name"><span className="text-xs text-gray-400">Edit below (multi-line supported)</span></Field>
        <Field label="Tagline"><Input value={d.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></Field>
      </div>
      <Field label="Logo"><ImageUpload value={d.logo_url} onChange={(v) => set("logo_url", v)} testid="site-logo-upload" /></Field>

      <div className="rounded-xl border border-gray-200 bg-[#F8FAFC] p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Live preview</div>
        <div onClick={(e) => e.preventDefault()} className="cursor-default">
          <Logo previewSite={d} />
        </div>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Logo size &amp; position</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Logo width (px)"><Input type="number" value={d.logo_width ?? 32} onChange={(e) => set("logo_width", Number(e.target.value))} data-testid="site-logo-width" /></Field>
        <Field label="Logo height (px)"><Input type="number" value={d.logo_height ?? 32} onChange={(e) => set("logo_height", Number(e.target.value))} data-testid="site-logo-height" /></Field>
        <Field label="Logo border radius (px)"><Input type="number" value={d.logo_radius ?? 8} onChange={(e) => set("logo_radius", Number(e.target.value))} /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Left spacing (px)"><Input type="number" value={d.logo_left_spacing ?? 0} onChange={(e) => set("logo_left_spacing", Number(e.target.value))} /></Field>
        <Field label="Top offset (px)"><Input type="number" value={d.logo_top_offset ?? 0} onChange={(e) => set("logo_top_offset", Number(e.target.value))} /></Field>
        <Field label="Bottom offset (px)"><Input type="number" value={d.logo_bottom_offset ?? 0} onChange={(e) => set("logo_bottom_offset", Number(e.target.value))} /></Field>
        <Field label="Right margin (px)"><Input type="number" value={d.logo_margin_right ?? 8} onChange={(e) => set("logo_margin_right", Number(e.target.value))} /></Field>
      </div>
      <p className="text-xs text-gray-400">Position offset nudges the logo without affecting surrounding spacing.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Position X (px)"><Input type="number" value={d.logo_offset_x ?? 0} onChange={(e) => set("logo_offset_x", Number(e.target.value))} /></Field>
        <Field label="Position Y (px)"><Input type="number" value={d.logo_offset_y ?? 0} onChange={(e) => set("logo_offset_y", Number(e.target.value))} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Company name style</div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Font size (px)"><Input type="number" value={d.company_name_font_size ?? 18} onChange={(e) => set("company_name_font_size", Number(e.target.value))} /></Field>
        <Field label="Font weight">
          <select value={d.company_name_font_weight ?? 600} onChange={(e) => set("company_name_font_weight", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Letter spacing (em)"><Input type="number" step="0.01" value={d.company_name_letter_spacing ?? -0.01} onChange={(e) => set("company_name_letter_spacing", Number(e.target.value))} /></Field>
        <Field label="Text color">
          <input type="color" value={d.company_name_color || "#0F172A"} onChange={(e) => set("company_name_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
      </div>
      <Field label="Vertical alignment">
        <select value={d.company_name_valign || "center"} onChange={(e) => set("company_name_valign", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white max-w-[200px]">
          <option value="top">Top</option>
          <option value="center">Center</option>
          <option value="bottom">Bottom</option>
        </select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Left margin (px)"><Input type="number" value={d.company_name_margin_left ?? 0} onChange={(e) => set("company_name_margin_left", Number(e.target.value))} /></Field>
        <Field label="Right margin (px)"><Input type="number" value={d.company_name_margin_right ?? 0} onChange={(e) => set("company_name_margin_right", Number(e.target.value))} /></Field>
      </div>
      <p className="text-xs text-gray-400">Position offset moves the name independently of the logo without affecting spacing.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Position X (px)"><Input type="number" value={d.company_name_offset_x ?? 0} onChange={(e) => set("company_name_offset_x", Number(e.target.value))} /></Field>
        <Field label="Position Y (px)"><Input type="number" value={d.company_name_offset_y ?? 0} onChange={(e) => set("company_name_offset_y", Number(e.target.value))} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Navigation settings</div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Font size (px)"><Input type="number" value={d.nav_font_size ?? 14} onChange={(e) => set("nav_font_size", Number(e.target.value))} /></Field>
        <Field label="Font weight">
          <select value={d.nav_font_weight ?? 500} onChange={(e) => set("nav_font_weight", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Letter spacing (em)"><Input type="number" step="0.01" value={d.nav_letter_spacing ?? 0} onChange={(e) => set("nav_letter_spacing", Number(e.target.value))} /></Field>
        <Field label="Item gap (px)"><Input type="number" value={d.nav_gap ?? 4} onChange={(e) => set("nav_gap", Number(e.target.value))} /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Text color">
          <input type="color" value={d.nav_text_color || "#4B5563"} onChange={(e) => set("nav_text_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Hover color">
          <input type="color" value={d.nav_hover_color || "#0F172A"} onChange={(e) => set("nav_hover_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Active color">
          <input type="color" value={d.nav_active_color || "#0F172A"} onChange={(e) => set("nav_active_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
      </div>
      <p className="text-xs text-gray-400 -mt-2">Applies to desktop and mobile navigation. Sizing scales down automatically on small screens via the browser's responsive layout — no separate tablet/mobile values needed.</p>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Header layout</div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!d.hide_login} onChange={(e) => set("hide_login", e.target.checked)} />
        Hide Sign In button (login page still accessible via URL)
      </label>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Header height (px)"><Input type="number" value={d.header_height ?? ""} placeholder="auto" onChange={(e) => set("header_height", e.target.value ? Number(e.target.value) : null)} /></Field>
        <Field label="Header max width (px)"><Input type="number" value={d.header_max_width ?? ""} placeholder="1280" onChange={(e) => set("header_max_width", e.target.value ? Number(e.target.value) : null)} /></Field>
        <Field label="Header border radius (px)"><Input type="number" value={d.header_border_radius ?? 0} onChange={(e) => set("header_border_radius", Number(e.target.value))} /></Field>
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Nav top padding (px)"><Input type="number" value={d.nav_padding_top ?? 10} onChange={(e) => set("nav_padding_top", Number(e.target.value))} /></Field>
        <Field label="Nav bottom padding (px)"><Input type="number" value={d.nav_padding_bottom ?? 10} onChange={(e) => set("nav_padding_bottom", Number(e.target.value))} /></Field>
        <Field label="Nav left padding (px)"><Input type="number" value={d.nav_padding_left ?? ""} placeholder="default" onChange={(e) => set("nav_padding_left", e.target.value ? Number(e.target.value) : null)} /></Field>
        <Field label="Nav right padding (px)"><Input type="number" value={d.nav_padding_right ?? ""} placeholder="default" onChange={(e) => set("nav_padding_right", e.target.value ? Number(e.target.value) : null)} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Company name (multi-line)</div>
      <Field label="Company name">
        <textarea value={d.company_name || ""} onChange={(e) => set("company_name", e.target.value)} rows={2} placeholder="Press Enter for line breaks" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm w-full resize-y" />
      </Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Max width (px)"><Input type="number" value={d.company_name_max_width ?? ""} placeholder="auto" onChange={(e) => set("company_name_max_width", e.target.value ? Number(e.target.value) : null)} /></Field>
        <Field label="Text alignment">
          <select value={d.company_name_text_align || "left"} onChange={(e) => set("company_name_text_align", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Logo extras</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Logo scale"><Input type="number" step="0.05" value={d.logo_scale ?? 1} onChange={(e) => set("logo_scale", Number(e.target.value))} /></Field>
        <Field label="Logo–name spacing (px)"><Input type="number" value={d.logo_name_gap ?? 8} onChange={(e) => set("logo_name_gap", Number(e.target.value))} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Tagline (shown under company name)</div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!d.tagline_show} onChange={(e) => set("tagline_show", e.target.checked)} data-testid="site-tagline-show" />
        Show tagline in header
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Tagline font size (px)"><Input type="number" value={d.tagline_font_size ?? 11} onChange={(e) => set("tagline_font_size", Number(e.target.value))} /></Field>
        <Field label="Tagline color">
          <input type="color" value={d.tagline_color || "#94A3B8"} onChange={(e) => set("tagline_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Header CTA text"><Input value={d.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} data-testid="site-cta-text" /></Field>
        <Field label="Header CTA link"><Input value={d.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} /></Field>
      </div>

      {/* ── Company name extras ──────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Company name — style extras</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Text transform">
          <select value={d.company_name_text_transform || "none"} onChange={(e) => set("company_name_text_transform", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            <option value="none">None</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="lowercase">lowercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </Field>
        <Field label="Opacity (0–1)"><Input type="number" step="0.05" min="0" max="1" value={d.company_name_opacity ?? 1} onChange={(e) => set("company_name_opacity", Number(e.target.value))} /></Field>
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1">Company name — top/bottom margin &amp; padding</div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Margin top (px)"><Input type="number" value={d.company_name_margin_top ?? 0} onChange={(e) => set("company_name_margin_top", Number(e.target.value))} /></Field>
        <Field label="Margin bottom (px)"><Input type="number" value={d.company_name_margin_bottom ?? 0} onChange={(e) => set("company_name_margin_bottom", Number(e.target.value))} /></Field>
        <Field label="Padding top (px)"><Input type="number" value={d.company_name_padding_top ?? 0} onChange={(e) => set("company_name_padding_top", Number(e.target.value))} /></Field>
        <Field label="Padding right (px)"><Input type="number" value={d.company_name_padding_right ?? 0} onChange={(e) => set("company_name_padding_right", Number(e.target.value))} /></Field>
        <Field label="Padding bottom (px)"><Input type="number" value={d.company_name_padding_bottom ?? 0} onChange={(e) => set("company_name_padding_bottom", Number(e.target.value))} /></Field>
        <Field label="Padding left (px)"><Input type="number" value={d.company_name_padding_left ?? 0} onChange={(e) => set("company_name_padding_left", Number(e.target.value))} /></Field>
      </div>

      {/* ── Tagline extras ───────────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Tagline — extra style</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Font weight">
          <select value={d.tagline_font_weight ?? 400} onChange={(e) => set("tagline_font_weight", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[300, 400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Letter spacing (em)"><Input type="number" step="0.01" min="-0.1" max="0.5" value={d.tagline_letter_spacing ?? 0} onChange={(e) => set("tagline_letter_spacing", Number(e.target.value))} /></Field>
      </div>

      {/* ── Logo spacing ─────────────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Logo — independent margin &amp; padding</div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Margin top (px)"><Input type="number" value={d.logo_margin_top ?? 0} onChange={(e) => set("logo_margin_top", Number(e.target.value))} /></Field>
        <Field label="Margin right (px)"><Input type="number" value={d.logo_margin_right ?? 0} onChange={(e) => set("logo_margin_right", Number(e.target.value))} /></Field>
        <Field label="Margin bottom (px)"><Input type="number" value={d.logo_margin_bottom ?? 0} onChange={(e) => set("logo_margin_bottom", Number(e.target.value))} /></Field>
        <Field label="Margin left (px)"><Input type="number" value={d.logo_margin_left ?? 0} onChange={(e) => set("logo_margin_left", Number(e.target.value))} /></Field>
        <Field label="Padding top (px)"><Input type="number" value={d.logo_padding_top ?? 0} onChange={(e) => set("logo_padding_top", Number(e.target.value))} /></Field>
        <Field label="Padding right (px)"><Input type="number" value={d.logo_padding_right ?? 0} onChange={(e) => set("logo_padding_right", Number(e.target.value))} /></Field>
        <Field label="Padding bottom (px)"><Input type="number" value={d.logo_padding_bottom ?? 0} onChange={(e) => set("logo_padding_bottom", Number(e.target.value))} /></Field>
        <Field label="Padding left (px)"><Input type="number" value={d.logo_padding_left ?? 0} onChange={(e) => set("logo_padding_left", Number(e.target.value))} /></Field>
      </div>

      {/* ── Nav item spacing ─────────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Navigation — item padding &amp; spacing</div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Item padding top (px)"><Input type="number" value={d.nav_item_padding_top ?? 8} onChange={(e) => set("nav_item_padding_top", Number(e.target.value))} /></Field>
        <Field label="Item padding right (px)"><Input type="number" value={d.nav_item_padding_right ?? 12} onChange={(e) => set("nav_item_padding_right", Number(e.target.value))} /></Field>
        <Field label="Item padding bottom (px)"><Input type="number" value={d.nav_item_padding_bottom ?? 8} onChange={(e) => set("nav_item_padding_bottom", Number(e.target.value))} /></Field>
        <Field label="Item padding left (px)"><Input type="number" value={d.nav_item_padding_left ?? 12} onChange={(e) => set("nav_item_padding_left", Number(e.target.value))} /></Field>
        <Field label="Item border radius (px)"><Input type="number" value={d.nav_item_border_radius ?? 8} onChange={(e) => set("nav_item_border_radius", Number(e.target.value))} /></Field>
        <Field label="Nav margin top (px)"><Input type="number" value={d.nav_margin_top ?? 0} onChange={(e) => set("nav_margin_top", Number(e.target.value))} /></Field>
        <Field label="Nav margin right (px)"><Input type="number" value={d.nav_margin_right ?? 0} onChange={(e) => set("nav_margin_right", Number(e.target.value))} /></Field>
        <Field label="Nav margin left (px)"><Input type="number" value={d.nav_margin_left ?? 0} onChange={(e) => set("nav_margin_left", Number(e.target.value))} /></Field>
      </div>

      {/* ── Sign In button ───────────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Sign In button</div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={d.signin_btn_visible !== false} onChange={(e) => set("signin_btn_visible", e.target.checked)} />
        Show Sign In button
      </label>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Button text"><Input value={d.signin_btn_text || "Sign in"} onChange={(e) => set("signin_btn_text", e.target.value)} /></Field>
        <Field label="Background color">
          <input type="color" value={d.signin_btn_bg && d.signin_btn_bg !== "transparent" ? d.signin_btn_bg : "#ffffff"} onChange={(e) => set("signin_btn_bg", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Text color">
          <input type="color" value={d.signin_btn_color || "#374151"} onChange={(e) => set("signin_btn_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Font size (px)"><Input type="number" value={d.signin_btn_font_size ?? 14} onChange={(e) => set("signin_btn_font_size", Number(e.target.value))} /></Field>
        <Field label="Font weight">
          <select value={d.signin_btn_font_weight ?? 500} onChange={(e) => set("signin_btn_font_weight", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Border radius (px)"><Input type="number" value={d.signin_btn_border_radius ?? 8} onChange={(e) => set("signin_btn_border_radius", Number(e.target.value))} /></Field>
        <Field label="Border style">
          <select value={d.signin_btn_border || "none"} onChange={(e) => set("signin_btn_border", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            <option value="none">None</option>
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
          </select>
        </Field>
        <Field label="H padding (px)"><Input type="number" value={d.signin_btn_padding_x ?? 12} onChange={(e) => set("signin_btn_padding_x", Number(e.target.value))} /></Field>
        <Field label="V padding (px)"><Input type="number" value={d.signin_btn_padding_y ?? 8} onChange={(e) => set("signin_btn_padding_y", Number(e.target.value))} /></Field>
        {d.signin_btn_border && d.signin_btn_border !== "none" && (
          <Field label="Border color">
            <input type="color" value={d.signin_btn_border_color || "#E2E8F0"} onChange={(e) => set("signin_btn_border_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
          </Field>
        )}
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Margin top (px)"><Input type="number" value={d.signin_btn_margin_top ?? 0} onChange={(e) => set("signin_btn_margin_top", Number(e.target.value))} /></Field>
        <Field label="Margin right (px)"><Input type="number" value={d.signin_btn_margin_right ?? 0} onChange={(e) => set("signin_btn_margin_right", Number(e.target.value))} /></Field>
        <Field label="Margin bottom (px)"><Input type="number" value={d.signin_btn_margin_bottom ?? 0} onChange={(e) => set("signin_btn_margin_bottom", Number(e.target.value))} /></Field>
        <Field label="Margin left (px)"><Input type="number" value={d.signin_btn_margin_left ?? 0} onChange={(e) => set("signin_btn_margin_left", Number(e.target.value))} /></Field>
      </div>

      {/* ── CTA / Book Now button ────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Book Now / CTA button</div>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={d.cta_btn_visible !== false} onChange={(e) => set("cta_btn_visible", e.target.checked)} />
        Show CTA button
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Background color">
          <input type="color" value={d.cta_btn_bg || "#0F172A"} onChange={(e) => set("cta_btn_bg", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Text color">
          <input type="color" value={d.cta_btn_color || "#FFFFFF"} onChange={(e) => set("cta_btn_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <Field label="Font size (px)"><Input type="number" value={d.cta_btn_font_size ?? 14} onChange={(e) => set("cta_btn_font_size", Number(e.target.value))} /></Field>
        <Field label="Font weight">
          <select value={d.cta_btn_font_weight ?? 500} onChange={(e) => set("cta_btn_font_weight", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="Border radius (px)"><Input type="number" value={d.cta_btn_border_radius ?? 8} onChange={(e) => set("cta_btn_border_radius", Number(e.target.value))} /></Field>
        <Field label="H padding (px)"><Input type="number" value={d.cta_btn_padding_x ?? 16} onChange={(e) => set("cta_btn_padding_x", Number(e.target.value))} /></Field>
        <Field label="V padding (px)"><Input type="number" value={d.cta_btn_padding_y ?? 8} onChange={(e) => set("cta_btn_padding_y", Number(e.target.value))} /></Field>
        <Field label="Margin top (px)"><Input type="number" value={d.cta_btn_margin_top ?? 0} onChange={(e) => set("cta_btn_margin_top", Number(e.target.value))} /></Field>
        <Field label="Margin right (px)"><Input type="number" value={d.cta_btn_margin_right ?? 0} onChange={(e) => set("cta_btn_margin_right", Number(e.target.value))} /></Field>
        <Field label="Margin bottom (px)"><Input type="number" value={d.cta_btn_margin_bottom ?? 0} onChange={(e) => set("cta_btn_margin_bottom", Number(e.target.value))} /></Field>
        <Field label="Margin left (px)"><Input type="number" value={d.cta_btn_margin_left ?? 0} onChange={(e) => set("cta_btn_margin_left", Number(e.target.value))} /></Field>
      </div>

      {/* ── Mobile header ────────────────────────────────────────── */}
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Mobile header</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Breakpoint (px) — hamburger shows below this">
          <select value={d.mobile_breakpoint ?? 768} onChange={(e) => set("mobile_breakpoint", Number(e.target.value))} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            {[480, 640, 768, 992, 1024].map((v) => <option key={v} value={v}>{v}px</option>)}
          </select>
        </Field>
        <Field label="Header height (px)"><Input type="number" value={d.mobile_header_height ?? 56} onChange={(e) => set("mobile_header_height", Number(e.target.value))} /></Field>
        <Field label="Header background">
          <input type="color" value={(d.mobile_header_bg || "#FFFFFF").replace(/rgba?\([^)]+\)/, "#FFFFFF")} onChange={(e) => set("mobile_header_bg", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Header padding top (px)"><Input type="number" value={d.mobile_header_padding_top ?? 10} onChange={(e) => set("mobile_header_padding_top", Number(e.target.value))} /></Field>
        <Field label="Header padding bottom (px)"><Input type="number" value={d.mobile_header_padding_bottom ?? 10} onChange={(e) => set("mobile_header_padding_bottom", Number(e.target.value))} /></Field>
        <Field label="Hamburger icon color">
          <input type="color" value={d.mobile_hamburger_color || "#374151"} onChange={(e) => set("mobile_hamburger_color", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Mobile logo width (px)"><Input type="number" value={d.mobile_logo_width ?? 28} onChange={(e) => set("mobile_logo_width", Number(e.target.value))} /></Field>
        <Field label="Mobile logo height (px)"><Input type="number" value={d.mobile_logo_height ?? 28} onChange={(e) => set("mobile_logo_height", Number(e.target.value))} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={d.mobile_show_cta !== false} onChange={(e) => set("mobile_show_cta", e.target.checked)} />
          Show CTA button in mobile header
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={d.mobile_show_signin !== false} onChange={(e) => set("mobile_show_signin", e.target.checked)} />
          Show Sign In in mobile menu
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={d.mobile_menu_overlay !== false} onChange={(e) => set("mobile_menu_overlay", e.target.checked)} />
          Show overlay behind mobile menu
        </label>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1">Mobile menu drawer</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Menu background">
          <input type="color" value={d.mobile_menu_bg || "#FFFFFF"} onChange={(e) => set("mobile_menu_bg", e.target.value)} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
        </Field>
        <Field label="Menu position">
          <select value={d.mobile_menu_position || "left"} onChange={(e) => set("mobile_menu_position", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            <option value="left">Slide from left</option>
            <option value="right">Slide from right</option>
            <option value="top">Drop from top</option>
          </select>
        </Field>
        <Field label="Menu animation">
          <select value={d.mobile_menu_animation || "slide"} onChange={(e) => set("mobile_menu_animation", e.target.value)} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm bg-white">
            <option value="slide">Slide</option>
            <option value="fade">Fade</option>
            <option value="none">None</option>
          </select>
        </Field>
        <Field label="Menu width (px)"><Input type="number" value={d.mobile_menu_width ?? 280} onChange={(e) => set("mobile_menu_width", Number(e.target.value))} /></Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Contact email"><Input value={d.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
        <Field label="Contact phone"><Input value={d.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
        <Field label="Address"><Input value={d.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
      </div>
      <Field label="Footer description">
        <TextArea value={d.footer_description || ""} placeholder="Empowering schools through innovative teacher training..." onChange={(e) => set("footer_description", e.target.value)} />
      </Field>
      <Field label="Social">
        <div className="grid sm:grid-cols-5 gap-2">
          {["linkedin", "twitter", "instagram", "youtube", "facebook"].map((k) => (
            <Input key={k} placeholder={k} value={(d.social || {})[k] || ""} onChange={(e) => setSocial(k, e.target.value)} />
          ))}
        </div>
      </Field>
      <button onClick={save} data-testid="site-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg w-fit"><Save className="w-4 h-4" /> Save changes</button>
    </div>
  );
}

// ── Shared KPI Editor — used by both Home ("Hero KPI Cards") and Programs
//    ("Programs KPI") panels. Same schema, same save/load, same icon/color/
//    typography/counter controls. `showFloatingPosition` toggles the
//    absolute-position controls that only make sense for Home's floating
//    badges-over-hero-image layout; Programs uses a grid instead (its own
//    columns/gap controls live in ProgramsPanel, outside this component). ──
function KpiEditorSection({ stats, setStats, title, hint, showFloatingPosition = true, testIdPrefix = "kpi" }) {
  const kpiDragIdx = useRef(null);
  const [expandedKpi, setExpandedKpi] = useState(null);

  const KPI_ICONS = [
    "Users","Award","Star","Shield","Zap","BookOpen","Calendar","GraduationCap",
    "ClipboardList","Send","Search","CheckCircle2","ShieldCheck","Sparkles","ArrowUpRight",
  ];
  const DEFAULT_GRADIENTS = [
    "linear-gradient(135deg, #3B68E7E6 0%, #3B68E799 100%)",
    "linear-gradient(135deg, #45B8ACE6 0%, #45B8AC99 100%)",
    "linear-gradient(135deg, #DDA756E6 0%, #DDA75699 100%)",
    "linear-gradient(135deg, #7C3AEDE6 0%, #7C3AED99 100%)",
    "linear-gradient(135deg, #059669E6 0%, #05966999 100%)",
    "linear-gradient(135deg, #DC2626E6 0%, #DC262699 100%)",
  ];

  const addKpi = () => {
    const newKpi = {
      id: `kpi_${Date.now()}`,
      k: "0", v: "New Metric",
      label: "New Metric", value: "0", prefix: "", suffix: "",
      subtitle: "", description: "",
      icon: "Star", iconUrl: "", iconPosition: "top",
      iconColor: "#FFFFFF", iconBg: "rgba(255,255,255,0.2)",
      iconContainerSize: 28, iconSize: 14, iconBorderRadius: 8, iconPadding: 0, iconShadow: "",
      gradient: DEFAULT_GRADIENTS[stats.length % DEFAULT_GRADIENTS.length],
      visible: true, order: stats.length,
      // counter
      counterEnabled: true, counterDuration: 1.8, counterDelay: stats.length * 0.15,
      counterDecimals: 0, counterSeparator: ",", counterStart: 0,
      counterFormat: "comma", maxDigits: null, leadingZero: false,
      // number typography
      numberColor: "#FFFFFF", numberFontSize: 24, numberFontWeight: 600,
      numberFontFamily: "", numberLetterSpacing: -0.02, numberLineHeight: 1.2,
      numberTextTransform: "none", numberTextShadow: "",
      // label typography
      labelColor: "rgba(255,255,255,0.9)", labelFontSize: 11, labelFontWeight: 400,
      labelFontFamily: "", labelLetterSpacing: 0, labelLineHeight: 1.3,
      // subtitle typography
      subtitleColor: "rgba(255,255,255,0.8)", subtitleFontSize: 10, subtitleFontWeight: 500,
      // description
      descriptionColor: "rgba(255,255,255,0.7)", descriptionFontSize: 9,
      // card layout
      cardWidth: 160, cardMinWidth: null, cardMaxWidth: null,
      cardBorderRadius: 24, cardPaddingX: 16, cardPaddingY: 16,
      cardRotation: 0, cardScale: 1, zIndex: null,
      // glass
      glassEnabled: true, glassBlur: 12,
      glassBorderColor: "rgba(255,255,255,0.4)", glassBorderWidth: 1,
      shadowColor: "rgba(0,0,0,0.12)", shadowBlur: 32, shadowSpread: 0,
      shadowOffsetX: 0, shadowOffsetY: 12,
      // hover
      hoverScale: 1.04, hoverLift: -4, hoverGlow: false,
      // position (desktop default — null means use built-in auto-position)
      posTop: null, posLeft: null, posRight: null, posBottom: null,
      posOffsetX: 0, posOffsetY: 0,
      // responsive positions
      posTablet: {}, posMobile: {},
    };
    setStats([...stats, newKpi]);
  };

  const updateKpi = (i, k, v) =>
    setStats(stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  // Updates multiple fields on one card in a single state update. Calling
  // updateKpi() twice in the same handler (e.g. once for "value", once for the
  // legacy "k" mirror) was a bug: both calls read from the same pre-update
  // `stats` snapshot, so the second call's setStats silently overwrote the
  // first — the typed value never actually reached the field the public page
  // reads first. This applies the whole patch atomically instead.
  const updateKpiMulti = (i, patch) =>
    setStats(stats.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const removeKpi = (i) => setStats(stats.filter((_, idx) => idx !== i));

  const duplicateKpi = (i) => {
    const copy = { ...stats[i], id: `kpi_${Date.now()}`, order: stats.length };
    setStats([...stats, copy]);
  };

  const toggleKpiVisible = (i) =>
    updateKpi(i, "visible", stats[i].visible === false ? true : false);

  const moveKpi = (i, dir) => {
    const next = [...stats];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStats(next.map((s, idx) => ({ ...s, order: idx })));
  };

  const onKpiDragStart = (i) => { kpiDragIdx.current = i; };
  const onKpiDragOver = (e, i) => {
    e.preventDefault();
    if (kpiDragIdx.current === null || kpiDragIdx.current === i) return;
    const next = [...stats];
    const [moved] = next.splice(kpiDragIdx.current, 1);
    next.splice(i, 0, moved);
    kpiDragIdx.current = i;
    setStats(next.map((s, idx) => ({ ...s, order: idx })));
  };

  const toggleKpiExpand = (i) => setExpandedKpi(expandedKpi === i ? null : i);

  return (
      // ── Hero KPI Cards ───────────────────────────────────────────────────
      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-medium text-[#0F172A]">{title}</div>
            <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
          </div>
          <button onClick={addKpi} data-testid={`${testIdPrefix}-add-kpi`}
            className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium shrink-0">
            <Plus className="w-3 h-3" /> Add KPI
          </button>
        </div>

        {stats.length === 0 && (
          <div className="text-xs text-gray-400 py-2">
            No KPI cards yet — add one above, or save to restore the defaults.
          </div>
        )}

        <div className="grid gap-3">
          {stats.map((kpi, i) => (
            <div
              key={kpi.id || i}
              draggable
              onDragStart={() => onKpiDragStart(i)}
              onDragOver={(e) => onKpiDragOver(e, i)}
              onDragEnd={() => { kpiDragIdx.current = null; }}
              data-testid={`${testIdPrefix}-kpi-row-${i}`}
              className={`rounded-xl border ${kpi.visible !== false ? "border-gray-200 bg-white" : "border-dashed border-gray-200 bg-gray-50"}`}
            >
              {/* Collapsed header */}
              <div className="flex items-center gap-2 p-3">
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab shrink-0" />
                <div className="w-8 h-8 rounded-lg shrink-0 border border-gray-200"
                  style={{ background: kpi.gradient || "#3B68E7" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0F172A] truncate">
                    {kpi.prefix}{kpi.value || kpi.k || "—"}{kpi.suffix}
                    {" "}<span className="text-gray-500 font-normal">{kpi.label || kpi.v}</span>
                  </div>
                  <div className="text-xs text-gray-400">{kpi.icon} · {kpi.counterEnabled !== false ? `Counter ${kpi.counterDuration ?? 1.8}s` : "Static"}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => moveKpi(i, -1)} disabled={i === 0}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 disabled:opacity-30" title="Move up">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveKpi(i, 1)} disabled={i === stats.length - 1}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 disabled:opacity-30" title="Move down">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleKpiVisible(i)}
                    title={kpi.visible !== false ? "Hide" : "Show"}
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200">
                    <Eye className={`w-3.5 h-3.5 ${kpi.visible !== false ? "text-gray-400" : "text-gray-200"}`} />
                  </button>
                  <button onClick={() => duplicateKpi(i)} title="Duplicate"
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 text-gray-400 hover:text-indigo-600">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removeKpi(i)} title="Delete"
                    className="w-7 h-7 grid place-items-center rounded-md border border-gray-200 text-rose-500 hover:bg-rose-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleKpiExpand(i)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${expandedKpi === i ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#4F46E5] text-[#4F46E5] bg-[#EEF2FF] hover:bg-[#4F46E5] hover:text-white"}`}>
                    {expandedKpi === i ? "Close" : "✏ Edit"}
                  </button>
                </div>
              </div>

              {/* Expanded editor */}
              {expandedKpi === i && (
                <div className="border-t border-gray-100 p-4 grid gap-4">

                  {/* ── Value ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Value</div>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Prefix (optional)">
                        <Input value={kpi.prefix || ""} placeholder="e.g. $"
                          onChange={(e) => updateKpi(i, "prefix", e.target.value)} />
                      </Field>
                      <Field label="Number value">
                        <Input value={kpi.value ?? kpi.k ?? ""} placeholder="e.g. 12400"
                          onChange={(e) => updateKpiMulti(i, { value: e.target.value, k: e.target.value })} />
                      </Field>
                      <Field label="Suffix (e.g. +, %, K)">
                        <Input value={kpi.suffix || ""} placeholder="e.g. +"
                          onChange={(e) => updateKpi(i, "suffix", e.target.value)} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Content ── */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Label">
                      <Input value={kpi.label || kpi.v || ""} placeholder="e.g. Teachers trained"
                        onChange={(e) => updateKpiMulti(i, { label: e.target.value, v: e.target.value })} />
                    </Field>
                    <Field label="Subtitle (optional — below label, italic)">
                      <Input value={kpi.subtitle || ""} placeholder="e.g. Across India"
                        onChange={(e) => updateKpi(i, "subtitle", e.target.value)} />
                    </Field>
                    <Field label="Description (optional — small text)">
                      <Input value={kpi.description || ""} placeholder="Short note below label"
                        onChange={(e) => updateKpi(i, "description", e.target.value)} />
                    </Field>
                  </div>

                  {/* ── Icon ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Icon</div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <Field label="Icon name (lucide)">
                        <select value={kpi.icon || "Star"}
                          onChange={(e) => updateKpi(i, "icon", e.target.value)}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          {KPI_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                        </select>
                      </Field>
                      <Field label="Icon color">
                        <input type="color" value={kpi.iconColor || "#FFFFFF"}
                          onChange={(e) => updateKpi(i, "iconColor", e.target.value)}
                          className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                      </Field>
                      <Field label="Icon bg CSS">
                        <Input value={kpi.iconBg || "rgba(255,255,255,0.2)"} placeholder="rgba(…)"
                          onChange={(e) => updateKpi(i, "iconBg", e.target.value)} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <Field label="Container size (px)">
                        <Input type="number" min={16} max={64} value={kpi.iconContainerSize ?? 28}
                          onChange={(e) => updateKpi(i, "iconContainerSize", Number(e.target.value))} />
                      </Field>
                      <Field label="Icon size (px)">
                        <Input type="number" min={8} max={48} value={kpi.iconSize ?? 14}
                          onChange={(e) => updateKpi(i, "iconSize", Number(e.target.value))} />
                      </Field>
                      <Field label="Border radius (px)">
                        <Input type="number" min={0} max={32} value={kpi.iconBorderRadius ?? 8}
                          onChange={(e) => updateKpi(i, "iconBorderRadius", Number(e.target.value))} />
                      </Field>
                      <Field label="Position">
                        <select value={kpi.iconPosition || "top"}
                          onChange={(e) => updateKpi(i, "iconPosition", e.target.value)}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          <option value="top">Top</option>
                          <option value="bottom">Bottom</option>
                        </select>
                      </Field>
                    </div>
                    <Field label="Custom icon (SVG/PNG — overrides lucide icon above)">
                      <ImageUpload value={kpi.iconUrl || ""} onChange={(v) => updateKpi(i, "iconUrl", v)}
                        testid={`kpi-icon-upload-${i}`} />
                    </Field>
                    {kpi.iconUrl && (
                      <button type="button" onClick={() => updateKpi(i, "iconUrl", "")}
                        className="text-xs text-rose-600 hover:underline mt-1">Remove custom icon</button>
                    )}
                    <div className="mt-2">
                      <Field label="Icon shadow CSS">
                        <Input value={kpi.iconShadow || ""} placeholder="0 2px 8px rgba(0,0,0,.3)"
                          onChange={(e) => updateKpi(i, "iconShadow", e.target.value)} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Card Appearance / Gradient ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Gradient &amp; Background</div>
                    <div className="grid sm:grid-cols-2 gap-2 mb-2">
                      <Field label="Gradient CSS">
                        <Input value={kpi.gradient || ""}
                          placeholder="linear-gradient(135deg, #3B68E7E6, #3B68E799)"
                          onChange={(e) => updateKpi(i, "gradient", e.target.value)} />
                      </Field>
                      <div className="grid grid-cols-3 gap-2">
                        {DEFAULT_GRADIENTS.map((g, gi) => (
                          <button key={gi} type="button"
                            onClick={() => updateKpi(i, "gradient", g)}
                            className={`h-8 rounded-lg border-2 transition-all ${kpi.gradient === g ? "border-[#4F46E5] scale-95" : "border-transparent"}`}
                            style={{ background: g }} title={`Preset ${gi + 1}`} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── Card Layout ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Card Size &amp; Layout</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Field label="Width (px)">
                        <Input type="number" min={80} max={400} value={kpi.cardWidth ?? 160}
                          onChange={(e) => updateKpi(i, "cardWidth", Number(e.target.value))} />
                      </Field>
                      <Field label="Min width (px)">
                        <Input type="number" min={0} max={400} value={kpi.cardMinWidth ?? ""}
                          placeholder="none"
                          onChange={(e) => updateKpi(i, "cardMinWidth", e.target.value ? Number(e.target.value) : null)} />
                      </Field>
                      <Field label="Max width (px)">
                        <Input type="number" min={0} max={600} value={kpi.cardMaxWidth ?? ""}
                          placeholder="none"
                          onChange={(e) => updateKpi(i, "cardMaxWidth", e.target.value ? Number(e.target.value) : null)} />
                      </Field>
                      <Field label="Border radius (px)">
                        <Input type="number" min={0} max={64} value={kpi.cardBorderRadius ?? 24}
                          onChange={(e) => updateKpi(i, "cardBorderRadius", Number(e.target.value))} />
                      </Field>
                      <Field label="Padding X (px)">
                        <Input type="number" min={0} max={48} value={kpi.cardPaddingX ?? 16}
                          onChange={(e) => updateKpi(i, "cardPaddingX", Number(e.target.value))} />
                      </Field>
                      <Field label="Padding Y (px)">
                        <Input type="number" min={0} max={48} value={kpi.cardPaddingY ?? 16}
                          onChange={(e) => updateKpi(i, "cardPaddingY", Number(e.target.value))} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Glassmorphism ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Glassmorphism</div>
                      <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" checked={kpi.glassEnabled !== false}
                          onChange={(e) => updateKpi(i, "glassEnabled", e.target.checked)} />
                        Enabled
                      </label>
                    </div>
                    {kpi.glassEnabled !== false && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                        <Field label="Blur (px)">
                          <Input type="number" min={0} max={40} value={kpi.glassBlur ?? 12}
                            onChange={(e) => updateKpi(i, "glassBlur", Number(e.target.value))} />
                        </Field>
                        <Field label="Border width (px)">
                          <Input type="number" min={0} max={8} value={kpi.glassBorderWidth ?? 1}
                            onChange={(e) => updateKpi(i, "glassBorderWidth", Number(e.target.value))} />
                        </Field>
                        <Field label="Border color CSS">
                          <Input value={kpi.glassBorderColor ?? "rgba(255,255,255,0.4)"}
                            placeholder="rgba(255,255,255,0.4)"
                            onChange={(e) => updateKpi(i, "glassBorderColor", e.target.value)} />
                        </Field>
                      </div>
                    )}
                    <div className="text-[10px] font-semibold text-gray-400 mb-1 mt-1">Shadow</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Field label="Shadow color CSS">
                        <Input value={kpi.shadowColor ?? "rgba(0,0,0,0.12)"}
                          placeholder="rgba(0,0,0,0.12)"
                          onChange={(e) => updateKpi(i, "shadowColor", e.target.value)} />
                      </Field>
                      <Field label="Blur (px)">
                        <Input type="number" min={0} max={80} value={kpi.shadowBlur ?? 32}
                          onChange={(e) => updateKpi(i, "shadowBlur", Number(e.target.value))} />
                      </Field>
                      <Field label="Spread (px)">
                        <Input type="number" min={-20} max={40} value={kpi.shadowSpread ?? 0}
                          onChange={(e) => updateKpi(i, "shadowSpread", Number(e.target.value))} />
                      </Field>
                      <Field label="Offset X (px)">
                        <Input type="number" min={-40} max={40} value={kpi.shadowOffsetX ?? 0}
                          onChange={(e) => updateKpi(i, "shadowOffsetX", Number(e.target.value))} />
                      </Field>
                      <Field label="Offset Y (px)">
                        <Input type="number" min={-40} max={80} value={kpi.shadowOffsetY ?? 12}
                          onChange={(e) => updateKpi(i, "shadowOffsetY", Number(e.target.value))} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Typography ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Typography — Number</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <Field label="Size (px)">
                        <Input type="number" min={12} max={64} value={kpi.numberFontSize ?? 24}
                          onChange={(e) => updateKpi(i, "numberFontSize", Number(e.target.value))} />
                      </Field>
                      <Field label="Weight">
                        <select value={kpi.numberFontWeight ?? 600}
                          onChange={(e) => updateKpi(i, "numberFontWeight", Number(e.target.value))}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </Field>
                      <Field label="Color">
                        <input type="color" value={kpi.numberColor || "#FFFFFF"}
                          onChange={(e) => updateKpi(i, "numberColor", e.target.value)}
                          className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                      </Field>
                      <Field label="Transform">
                        <select value={kpi.numberTextTransform || "none"}
                          onChange={(e) => updateKpi(i, "numberTextTransform", e.target.value)}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          <option value="none">None</option>
                          <option value="uppercase">UPPER</option>
                          <option value="lowercase">lower</option>
                        </select>
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                      <Field label="Font family">
                        <Input value={kpi.numberFontFamily || ""} placeholder="Inter (default)"
                          onChange={(e) => updateKpi(i, "numberFontFamily", e.target.value)} />
                      </Field>
                      <Field label="Letter spacing (em)">
                        <Input type="number" step="0.01" min="-0.1" max="0.3" value={kpi.numberLetterSpacing ?? -0.02}
                          onChange={(e) => updateKpi(i, "numberLetterSpacing", Number(e.target.value))} />
                      </Field>
                      <Field label="Line height">
                        <Input type="number" step="0.05" min="1" max="3" value={kpi.numberLineHeight ?? 1.2}
                          onChange={(e) => updateKpi(i, "numberLineHeight", Number(e.target.value))} />
                      </Field>
                    </div>
                    <Field label="Text shadow CSS">
                      <Input value={kpi.numberTextShadow || ""} placeholder="1px 1px 2px rgba(0,0,0,.3)"
                        onChange={(e) => updateKpi(i, "numberTextShadow", e.target.value)} />
                    </Field>
                  </div>

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Typography — Label</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <Field label="Size (px)">
                        <Input type="number" min={8} max={24} value={kpi.labelFontSize ?? 11}
                          onChange={(e) => updateKpi(i, "labelFontSize", Number(e.target.value))} />
                      </Field>
                      <Field label="Weight">
                        <select value={kpi.labelFontWeight ?? 400}
                          onChange={(e) => updateKpi(i, "labelFontWeight", Number(e.target.value))}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          {[400, 500, 600, 700].map((w) => <option key={w} value={w}>{w}</option>)}
                        </select>
                      </Field>
                      <Field label="Color">
                        <input type="color"
                          value={kpi.labelColor && kpi.labelColor !== "rgba(255,255,255,0.9)" ? kpi.labelColor : "#FFFFFF"}
                          onChange={(e) => updateKpi(i, "labelColor", e.target.value)}
                          className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                      </Field>
                      <Field label="Letter spacing (em)">
                        <Input type="number" step="0.01" min="-0.1" max="0.3" value={kpi.labelLetterSpacing ?? 0}
                          onChange={(e) => updateKpi(i, "labelLetterSpacing", Number(e.target.value))} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Font family">
                        <Input value={kpi.labelFontFamily || ""} placeholder="Inter (default)"
                          onChange={(e) => updateKpi(i, "labelFontFamily", e.target.value)} />
                      </Field>
                      <Field label="Line height">
                        <Input type="number" step="0.05" min="1" max="3" value={kpi.labelLineHeight ?? 1.3}
                          onChange={(e) => updateKpi(i, "labelLineHeight", Number(e.target.value))} />
                      </Field>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Typography — Description</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Size (px)">
                        <Input type="number" min={7} max={16} value={kpi.descriptionFontSize ?? 9}
                          onChange={(e) => updateKpi(i, "descriptionFontSize", Number(e.target.value))} />
                      </Field>
                      <Field label="Color CSS">
                        <Input value={kpi.descriptionColor || "rgba(255,255,255,0.7)"}
                          placeholder="rgba(255,255,255,0.7)"
                          onChange={(e) => updateKpi(i, "descriptionColor", e.target.value)} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Counter Animation ── */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Counter Animation</div>
                      <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" checked={kpi.counterEnabled !== false}
                          onChange={(e) => updateKpi(i, "counterEnabled", e.target.checked)} />
                        Enabled
                      </label>
                    </div>
                    {kpi.counterEnabled !== false && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <Field label="Duration (s)">
                          <Input type="number" min={0.1} max={10} step={0.1} value={kpi.counterDuration ?? 1.8}
                            onChange={(e) => updateKpi(i, "counterDuration", Number(e.target.value))} />
                        </Field>
                        <Field label="Delay (s)">
                          <Input type="number" min={0} max={5} step={0.05} value={kpi.counterDelay ?? 0}
                            onChange={(e) => updateKpi(i, "counterDelay", Number(e.target.value))} />
                        </Field>
                        <Field label="Decimal places">
                          <Input type="number" min={0} max={4} value={kpi.counterDecimals ?? 0}
                            onChange={(e) => updateKpi(i, "counterDecimals", Number(e.target.value))} />
                        </Field>
                        <Field label="Start value">
                          <Input type="number" value={kpi.counterStart ?? 0}
                            onChange={(e) => updateKpi(i, "counterStart", Number(e.target.value))} />
                        </Field>
                        <Field label="Separator">
                          <Input value={kpi.counterSeparator ?? ","} placeholder=","
                            onChange={(e) => updateKpi(i, "counterSeparator", e.target.value)} />
                        </Field>
                      </div>
                    )}
                  </div>

                  {/* ── Counter Format ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Counter Format</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Field label="Format">
                        <select value={kpi.counterFormat || "comma"}
                          onChange={(e) => updateKpi(i, "counterFormat", e.target.value)}
                          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white w-full">
                          <option value="normal">Normal (12400)</option>
                          <option value="comma">Comma (12,400)</option>
                          <option value="percentage">Percentage (94%)</option>
                          <option value="currency">Currency (₹12,400)</option>
                          <option value="custom">Custom (use prefix/suffix)</option>
                        </select>
                      </Field>
                      <Field label="Max digits (blank = no limit)">
                        <Input type="number" min={1} max={12} value={kpi.maxDigits ?? ""}
                          placeholder="none"
                          onChange={(e) => updateKpi(i, "maxDigits", e.target.value ? Number(e.target.value) : null)} />
                      </Field>
                      <label className="flex items-center gap-2 text-sm text-gray-600 pt-6">
                        <input type="checkbox" checked={!!kpi.leadingZero}
                          onChange={(e) => updateKpi(i, "leadingZero", e.target.checked)} />
                        Leading zero
                      </label>
                    </div>
                  </div>

                  {showFloatingPosition && (
                    <>
                  {/* ── Floating Position ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Floating Position (Desktop)
                      <span className="ml-1 text-gray-300 normal-case font-normal">— blank = auto layout</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <Field label="Top (e.g. 12px, 10%)">
                        <Input value={kpi.posTop ?? ""} placeholder="auto"
                          onChange={(e) => updateKpi(i, "posTop", e.target.value || null)} />
                      </Field>
                      <Field label="Bottom">
                        <Input value={kpi.posBottom ?? ""} placeholder="auto"
                          onChange={(e) => updateKpi(i, "posBottom", e.target.value || null)} />
                      </Field>
                      <Field label="Left">
                        <Input value={kpi.posLeft ?? ""} placeholder="auto"
                          onChange={(e) => updateKpi(i, "posLeft", e.target.value || null)} />
                      </Field>
                      <Field label="Right">
                        <Input value={kpi.posRight ?? ""} placeholder="auto"
                          onChange={(e) => updateKpi(i, "posRight", e.target.value || null)} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Field label="Offset X (px)">
                        <Input type="number" value={kpi.posOffsetX ?? 0}
                          onChange={(e) => updateKpi(i, "posOffsetX", Number(e.target.value))} />
                      </Field>
                      <Field label="Offset Y (px)">
                        <Input type="number" value={kpi.posOffsetY ?? 0}
                          onChange={(e) => updateKpi(i, "posOffsetY", Number(e.target.value))} />
                      </Field>
                      <Field label="Z-index">
                        <Input type="number" value={kpi.zIndex ?? ""} placeholder="auto"
                          onChange={(e) => updateKpi(i, "zIndex", e.target.value ? Number(e.target.value) : null)} />
                      </Field>
                      <Field label="Rotation (deg)">
                        <Input type="number" min={-180} max={180} value={kpi.cardRotation ?? 0}
                          onChange={(e) => updateKpi(i, "cardRotation", Number(e.target.value))} />
                      </Field>
                    </div>
                  </div>

                  {/* ── Tablet Position ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Tablet Position (≤1023px) — blank = use desktop
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[["top","Top"],["bottom","Bottom"],["left","Left"],["right","Right"]].map(([k, lbl]) => (
                        <Field key={k} label={lbl}>
                          <Input value={(kpi.posTablet || {})[k] ?? ""} placeholder="auto"
                            onChange={(e) => updateKpi(i, "posTablet", {
                              ...(kpi.posTablet || {}),
                              [k]: e.target.value || undefined,
                            })} />
                        </Field>
                      ))}
                    </div>
                  </div>

                  {/* ── Mobile Position ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      Mobile Position (≤639px) — blank = use desktop
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[["top","Top"],["bottom","Bottom"],["left","Left"],["right","Right"]].map(([k, lbl]) => (
                        <Field key={k} label={lbl}>
                          <Input value={(kpi.posMobile || {})[k] ?? ""} placeholder="auto"
                            onChange={(e) => updateKpi(i, "posMobile", {
                              ...(kpi.posMobile || {}),
                              [k]: e.target.value || undefined,
                            })} />
                        </Field>
                      ))}
                    </div>
                  </div>

                    </>
                  )}
                  {/* ── Hover Animation ── */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Hover Animation</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Field label="Scale (e.g. 1.04)">
                        <Input type="number" min={1} max={1.2} step={0.01} value={kpi.hoverScale ?? 1.04}
                          onChange={(e) => updateKpi(i, "hoverScale", Number(e.target.value))} />
                      </Field>
                      <Field label="Lift / Y offset (px)">
                        <Input type="number" min={-20} max={0} value={kpi.hoverLift ?? -4}
                          onChange={(e) => updateKpi(i, "hoverLift", Number(e.target.value))} />
                      </Field>
                      <label className="flex items-center gap-2 text-sm text-gray-600 pt-6">
                        <input type="checkbox" checked={!!kpi.hoverGlow}
                          onChange={(e) => updateKpi(i, "hoverGlow", e.target.checked)} />
                        Glow effect
                      </label>
                    </div>
                  </div>

                  {/* ── Visibility ── */}
                  <div className="space-y-2 pt-1 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={kpi.visible !== false}
                          onChange={(e) => updateKpi(i, "visible", e.target.checked)} />
                        Visible on homepage
                      </label>
                      <span className="text-xs text-gray-400">Order: {kpi.order ?? i}</span>
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-2">Device Visibility</div>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={kpi.desktop_visible !== false}
                          onChange={(e) => updateKpi(i, "desktop_visible", e.target.checked)} />
                        Show on Desktop
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={kpi.tablet_visible !== false}
                          onChange={(e) => updateKpi(i, "tablet_visible", e.target.checked)} />
                        Show on Tablet
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={kpi.mobile_visible === true}
                          onChange={(e) => updateKpi(i, "mobile_visible", e.target.checked)} />
                        Show on Mobile <span className="text-xs text-gray-400">(default: hidden)</span>
                      </label>
                    </div>
                  </div>

                </div>
              )}
            </div>
          ))}
        </div>
      </div>
  );
}


function HomePanel() {
  const { reload } = useCms();
  const [d, setD] = useEditable("/cms/home");

  // ── All hooks MUST be before any early return (Rules of Hooks) ────────────
  const kpiDragIdx = useRef(null);
  const [expandedKpi, setExpandedKpi] = useState(null);

  if (!d) return <div className="text-gray-500">Loading…</div>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const setArr = (k, v) => setD({ ...d, [k]: v });
  const save = async () => { await api.put("/cms/home", d); await reload(); toast.success("Home saved"); };

  // ── Hero KPI stats ─────────────────────────────────────────────────────────
  const stats = Array.isArray(d.stats) ? d.stats : [];
  const setStats = (s) => setD({ ...d, stats: s });

  // ── Why Choose Us ──────────────────────────────────────────────────────────
  const features = d.why_choose_us || [];
  const updateFeature = (i, k, v) => setD({ ...d, why_choose_us: features.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)) });
  const addFeature = () => setD({ ...d, why_choose_us: [...features, { icon: "BookOpen", title: "", description: "", visible: true }] });
  const removeFeature = (i) => setD({ ...d, why_choose_us: features.filter((_, idx) => idx !== i) });
  const ICON_OPTIONS = ["BookOpen", "Calendar", "Award", "Users", "Shield", "Zap", "GraduationCap", "ClipboardList", "Send", "Search", "CheckCircle2", "ShieldCheck", "Star", "Sparkles"];

  const journeySteps = d.training_journey || [];
  const updateJourneyStep = (i, k, v) => setD({ ...d, training_journey: journeySteps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) });
  const addJourneyStep = () => setD({ ...d, training_journey: [...journeySteps, { icon: "BookOpen", title: "", description: "", color: "#2A428D", link: "", button_text: "", visible: true }] });
  const removeJourneyStep = (i) => setD({ ...d, training_journey: journeySteps.filter((_, idx) => idx !== i) });

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4 max-w-3xl">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Eyebrow"><Input value={d.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} data-testid="home-eyebrow" /></Field>
        <Field label="Hero Media">
          <ImageUpload value={d.hero_image} onChange={(v) => set("hero_image", v)} testid="home-hero-image" />
          <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP, or animated GIF. GIFs play automatically on the homepage.</p>
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Hero heading line 1"><Input value={d.hero_heading_l1 || ""} onChange={(e) => set("hero_heading_l1", e.target.value)} data-testid="home-h1" /></Field>
        <Field label="Hero heading line 2 (colored)"><Input value={d.hero_heading_l2 || ""} onChange={(e) => set("hero_heading_l2", e.target.value)} data-testid="home-h2" /></Field>
      </div>
      <Field label="Hero description"><TextArea value={d.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Primary CTA text"><Input value={d.primary_cta_text || ""} onChange={(e) => set("primary_cta_text", e.target.value)} /></Field>
        <Field label="Primary CTA link"><Input value={d.primary_cta_link || ""} onChange={(e) => set("primary_cta_link", e.target.value)} /></Field>
        <Field label="Secondary CTA text"><Input value={d.secondary_cta_text || ""} onChange={(e) => set("secondary_cta_text", e.target.value)} /></Field>
        <Field label="Secondary CTA link"><Input value={d.secondary_cta_link || ""} onChange={(e) => set("secondary_cta_link", e.target.value)} /></Field>
      </div>
      <Field label="Trust bullets (one per line)">
        <TextArea value={(d.trust_bullets || []).join("\n")} onChange={(e) => setArr("trust_bullets", e.target.value.split("\n").filter(Boolean))} />
      </Field>
      <Field label="Partner names (one per line)">
        <TextArea value={(d.partners || []).join("\n")} onChange={(e) => setArr("partners", e.target.value.split("\n").filter(Boolean))} />
      </Field>

      <KpiEditorSection
        stats={stats}
        setStats={setStats}
        title="Hero KPI Cards"
        hint="Floating glass metric badges shown over the hero image. Drag to reorder. Up to 6 shown. Changes save with the page."
        showFloatingPosition={true}
        testIdPrefix="home"
      />


      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-medium text-[#0F172A]">Why Choose Us</div>
            <div className="text-xs text-gray-500 mt-0.5">Order matches display order. Uncheck to hide without deleting.</div>
          </div>
          <button onClick={addFeature} data-testid="add-feature" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium">
            <Plus className="w-3 h-3" /> Add feature
          </button>
        </div>
        <div className="grid gap-3">
          {features.map((f, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[100px,1fr,1fr,auto,auto] gap-2 items-start" data-testid={`feature-row-${i}`}>
              <select value={f.icon} onChange={(e) => updateFeature(i, "icon", e.target.value)}
                className="px-2 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white">
                {ICON_OPTIONS.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
              <Input value={f.title} placeholder="Title" onChange={(e) => updateFeature(i, "title", e.target.value)} />
              <Input value={f.description} placeholder="Description" onChange={(e) => updateFeature(i, "description", e.target.value)} />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 px-1 py-2.5" title="Visible on homepage">
                <input type="checkbox" checked={f.visible !== false} onChange={(e) => updateFeature(i, "visible", e.target.checked)} /> Visible
              </label>
              <button onClick={() => removeFeature(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg" aria-label="Remove feature">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {features.length === 0 && <div className="text-xs text-gray-400">No features yet — add one above.</div>}
        </div>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <Field label="Training journey heading">
          <Input value={d.training_journey_heading || ""} placeholder="Training Journey Timeline" onChange={(e) => set("training_journey_heading", e.target.value)} data-testid="journey-heading" />
        </Field>
        <Field label="Training journey subtitle">
          <Input value={d.training_journey_subtitle || ""} placeholder="Follow a simple journey from registration to classroom implementation." onChange={(e) => set("training_journey_subtitle", e.target.value)} />
        </Field>
        <div className="flex items-center justify-between mb-3 mt-4">
          <div>
            <div className="text-sm font-medium text-[#0F172A]">Training Journey Steps</div>
            <div className="text-xs text-gray-500 mt-0.5">Order matches display order. Color is any hex value — icon contrast adjusts automatically.</div>
          </div>
          <button onClick={addJourneyStep} data-testid="add-journey-step" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium">
            <Plus className="w-3 h-3" /> Add step
          </button>
        </div>
        <div className="grid gap-4">
          {journeySteps.map((s, i) => (
            <div key={i} className="rounded-xl border border-gray-100 p-3 grid gap-2" data-testid={`journey-row-${i}`}>
              <div className="grid grid-cols-2 sm:grid-cols-[90px,100px,1fr,1fr] gap-2">
                <select value={s.icon} onChange={(e) => updateJourneyStep(i, "icon", e.target.value)} className="px-2 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white">
                  {ICON_OPTIONS.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
                <input type="color" value={s.color || "#2A428D"} onChange={(e) => updateJourneyStep(i, "color", e.target.value)} className="h-[42px] rounded-lg border border-gray-200 cursor-pointer" />
                <Input value={s.title} placeholder="Title" onChange={(e) => updateJourneyStep(i, "title", e.target.value)} />
                <Input value={s.description} placeholder="Description" onChange={(e) => updateJourneyStep(i, "description", e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto,auto] gap-2 items-center">
                <Input value={s.link || ""} placeholder="Optional link" onChange={(e) => updateJourneyStep(i, "link", e.target.value)} />
                <Input value={s.button_text || ""} placeholder="Optional button text" onChange={(e) => updateJourneyStep(i, "button_text", e.target.value)} />
                <label className="flex items-center gap-1.5 text-xs text-gray-600" title="Visible on homepage">
                  <input type="checkbox" checked={s.visible !== false} onChange={(e) => updateJourneyStep(i, "visible", e.target.checked)} /> Visible
                </label>
                <button onClick={() => removeJourneyStep(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg justify-self-end" aria-label="Remove step">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {journeySteps.length === 0 && <div className="text-xs text-gray-400">No steps yet — add one above.</div>}
        </div>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <Field label="Meet the Experts heading">
          <Input value={d.meet_experts_heading || ""} placeholder="Meet the Experts" onChange={(e) => set("meet_experts_heading", e.target.value)} data-testid="experts-heading" />
        </Field>
        <Field label="Meet the Experts subtitle">
          <TextArea value={d.meet_experts_subtitle || ""} placeholder="Experienced educators, pedagogy researchers, school leaders and master trainers..." onChange={(e) => set("meet_experts_subtitle", e.target.value)} />
        </Field>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-[#0F172A]">Certificate Verification CTA</div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={d.verify_cta_active !== false} onChange={(e) => set("verify_cta_active", e.target.checked)} data-testid="verify-cta-active" /> Active
          </label>
        </div>
        <Field label="Heading">
          <Input value={d.verify_cta_heading || ""} placeholder="Certificate Verification" onChange={(e) => set("verify_cta_heading", e.target.value)} data-testid="verify-cta-heading-field" />
        </Field>
        <Field label="Description">
          <TextArea value={d.verify_cta_description || ""} placeholder="Instantly verify teacher certificates issued through our training platform..." onChange={(e) => set("verify_cta_description", e.target.value)} />
        </Field>
        <Field label="QR destination URL (leave blank to default to /verify)">
          <div className="flex items-start gap-3">
            <Input value={d.verify_qr_url || ""} placeholder="https://yourdomain.com/verify"
              onChange={(e) => set("verify_qr_url", e.target.value)} data-testid="verify-qr-url" />
            <div className="shrink-0 text-center">
              <img src={`${BACKEND}/api/qr/verify`} alt="Current QR" className="w-14 h-14 rounded-lg border border-gray-200" />
              <div className="text-[10px] text-gray-400 mt-1">Last saved</div>
            </div>
          </div>
          {d.verify_qr_url && !/^https?:\/\//.test(d.verify_qr_url) && (
            <p className="text-xs text-rose-600 mt-1">Must start with http:// or https://</p>
          )}
        </Field>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <Field label="Testimonials heading">
          <Input value={d.testimonials_heading || ""} placeholder="Testimonials" onChange={(e) => set("testimonials_heading", e.target.value)} data-testid="testimonials-heading" />
        </Field>
        <Field label="Testimonials subtitle">
          <TextArea value={d.testimonials_subtitle || ""} placeholder="See how educators and school leaders are transforming teaching..." onChange={(e) => set("testimonials_subtitle", e.target.value)} />
        </Field>
      </div>

      <button onClick={save} data-testid="home-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg w-fit"><Save className="w-4 h-4" /> Save changes</button>
    </div>
  );
}

function ProgramsPanel() {
  const { reload } = useCms();
  const [d, setD] = useEditable("/cms/programs");

  if (!d) return <div className="text-gray-500">Loading…</div>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => { await api.put("/cms/programs", d); await reload(); toast.success("Programs page saved"); };

  const stats = Array.isArray(d.stats) ? d.stats : [];
  const setStats = (s) => setD({ ...d, stats: s });

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4 max-w-3xl">
      <div>
        <div className="text-sm font-medium text-[#0F172A]">Programs KPI Grid Layout</div>
        <div className="text-xs text-gray-500 mt-0.5">Controls the container the KPI cards sit in — independent from the Home KPI layout.</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Columns — Desktop">
          <Input type="number" min={1} max={6} value={d.kpi_columns_desktop ?? 4}
            onChange={(e) => set("kpi_columns_desktop", Number(e.target.value))} />
        </Field>
        <Field label="Columns — Tablet">
          <Input type="number" min={1} max={4} value={d.kpi_columns_tablet ?? 2}
            onChange={(e) => set("kpi_columns_tablet", Number(e.target.value))} />
        </Field>
        <Field label="Columns — Mobile">
          <Input type="number" min={1} max={2} value={d.kpi_columns_mobile ?? 2}
            onChange={(e) => set("kpi_columns_mobile", Number(e.target.value))} />
        </Field>
        <Field label="Gap between cards (px)">
          <Input type="number" min={0} max={40} value={d.kpi_gap ?? 1}
            onChange={(e) => set("kpi_gap", Number(e.target.value))} />
        </Field>
        <Field label="Card padding X (px)">
          <Input type="number" min={0} max={48} value={d.kpi_card_padding_x ?? 20}
            onChange={(e) => set("kpi_card_padding_x", Number(e.target.value))} />
        </Field>
        <Field label="Card padding Y (px)">
          <Input type="number" min={0} max={48} value={d.kpi_card_padding_y ?? 24}
            onChange={(e) => set("kpi_card_padding_y", Number(e.target.value))} />
        </Field>
        <Field label="Card border radius (px)">
          <Input type="number" min={0} max={40} value={d.kpi_card_border_radius ?? 20}
            onChange={(e) => set("kpi_card_border_radius", Number(e.target.value))} />
        </Field>
        <Field label="Alignment">
          <select value={d.kpi_alignment || "left"} onChange={(e) => set("kpi_alignment", e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
            <option value="left">Left</option>
            <option value="center">Center</option>
          </select>
        </Field>
      </div>

      <KpiEditorSection
        stats={stats}
        setStats={setStats}
        title="Programs KPI"
        hint="Cards shown in the Programs page hero grid. Drag to reorder. Changes save with the page."
        showFloatingPosition={false}
        testIdPrefix="programs"
      />

      <button onClick={save} data-testid="programs-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg w-fit"><Save className="w-4 h-4" /> Save changes</button>
    </div>
  );
}

function AboutPanel() {
  const { reload } = useCms();
  const [d, setD] = useEditable("/cms/about");
  const [saving, setSaving] = useState(false);
  if (!d) return <div className="text-gray-500">Loading…</div>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const setFounder = (k, v) => setD({ ...d, founder: { ...(d.founder || {}), [k]: v } });
  const setFounderList = (k, txt) =>
    setFounder(k, txt.split("\n").map((s) => s.trim()).filter(Boolean));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/about", d);
      await reload();
      toast.success("About saved · public page will update in real-time");
    } catch {
      toast.error("Could not save. Please retry.");
    } finally {
      setSaving(false);
    }
  };

  const timeline = Array.isArray(d.timeline) ? d.timeline : [];
  const setTimeline = (tl) => set("timeline", tl);
  const addTimeline = () =>
    setTimeline([...timeline, { year: "", title: "", body: "" }]);
  const removeTimeline = (i) => setTimeline(timeline.filter((_, idx) => idx !== i));
  const updateTimeline = (i, k, v) =>
    setTimeline(timeline.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const achievements = Array.isArray(d.achievements) ? d.achievements : [];
  const setAch = (a) => set("achievements", a);
  const addAch = () => setAch([...achievements, { k: "", v: "" }]);
  const removeAch = (i) => setAch(achievements.filter((_, idx) => idx !== i));
  const updateAch = (i, k, v) =>
    setAch(achievements.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const why = Array.isArray(d.why_choose) ? d.why_choose : [];
  const setWhy = (a) => set("why_choose", a);
  const addWhy = () => setWhy([...why, { icon: "Sparkles", title: "", body: "" }]);
  const removeWhy = (i) => setWhy(why.filter((_, idx) => idx !== i));
  const updateWhy = (i, k, v) => setWhy(why.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  return (
    <div className="grid gap-6 max-w-4xl">
      {/* Autosave hint + Save button pinned at top for long form */}
      <div className="sticky top-0 z-10 -mx-2 px-2 py-3 bg-white/85 backdrop-blur border-b border-gray-200 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Every field below is live on the public <span className="font-medium text-[#0F172A]">/about</span> page.
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="about-save"
          className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 rounded-lg"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Hero + intro */}
      <SectionCard title="Hero" subtitle="Top of the About page.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow">
            <Input value={d.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} />
          </Field>
          <Field label="Headline">
            <Input value={d.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="about-title" />
          </Field>
        </div>
        <Field label="Intro paragraph">
          <TextArea value={d.body || ""} onChange={(e) => set("body", e.target.value)} />
        </Field>
      </SectionCard>

      {/* Founder */}
      <SectionCard title="Founder / Director" subtitle="The centrepiece of the About page — everything here becomes the founder spotlight.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name">
            <Input value={d.founder?.name || ""} onChange={(e) => setFounder("name", e.target.value)} data-testid="founder-name" />
          </Field>
          <Field label="Designation">
            <Input value={d.founder?.designation || ""} onChange={(e) => setFounder("designation", e.target.value)} data-testid="founder-designation" />
          </Field>
        </div>
        <Field label="Photo">
          <ImageUpload value={d.founder?.image} onChange={(v) => setFounder("image", v)} testid="founder-photo" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Experience (e.g. 18 yrs)">
            <Input value={d.founder?.experience || ""} onChange={(e) => setFounder("experience", e.target.value)} data-testid="founder-experience" />
          </Field>
          <Field label="Specialization">
            <Input value={d.founder?.specialization || ""} onChange={(e) => setFounder("specialization", e.target.value)} data-testid="founder-specialization" />
          </Field>
        </div>
        <Field label="Biography">
          <TextArea value={d.founder?.bio || ""} onChange={(e) => setFounder("bio", e.target.value)} data-testid="founder-bio" />
        </Field>
        <Field label="Personal message (quote block)">
          <TextArea value={d.founder?.personal_message || ""} onChange={(e) => setFounder("personal_message", e.target.value)} data-testid="founder-personal-message" placeholder="Every teacher we train comes back to a classroom that will remember them…" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Signature (below quote)">
            <Input value={d.founder?.signature || ""} onChange={(e) => setFounder("signature", e.target.value)} placeholder="e.g. Priya" />
          </Field>
          <Field label="Vision statement">
            <Input value={d.founder?.vision_statement || ""} onChange={(e) => setFounder("vision_statement", e.target.value)} />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Qualifications (one per line)">
            <TextArea value={(d.founder?.qualifications || []).join("\n")} onChange={(e) => setFounderList("qualifications", e.target.value)} data-testid="founder-qualifications" />
          </Field>
          <Field label="Awards (one per line)">
            <TextArea value={(d.founder?.awards || []).join("\n")} onChange={(e) => setFounderList("awards", e.target.value)} data-testid="founder-awards" />
          </Field>
        </div>
        <Field label="Achievements (one per line)">
          <TextArea value={(d.founder?.achievements || []).join("\n")} onChange={(e) => setFounderList("achievements", e.target.value)} data-testid="founder-achievements" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email">
            <Input value={d.founder?.email || ""} onChange={(e) => setFounder("email", e.target.value)} placeholder="founder@edulift.com" />
          </Field>
          <Field label="LinkedIn URL">
            <Input value={d.founder?.linkedin || ""} onChange={(e) => setFounder("linkedin", e.target.value)} />
          </Field>
        </div>
      </SectionCard>

      {/* Our Story */}
      <SectionCard title="Our Story" subtitle="Mission / Vision / Journey / Values shown as four premium cards.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Vision">
            <TextArea value={d.vision || ""} onChange={(e) => set("vision", e.target.value)} />
          </Field>
          <Field label="Mission">
            <TextArea value={d.mission || ""} onChange={(e) => set("mission", e.target.value)} />
          </Field>
        </div>
        <Field label="Journey">
          <TextArea value={d.journey || ""} onChange={(e) => set("journey", e.target.value)} placeholder="From a two-person studio to the platform behind 200+ schools…" />
        </Field>
        <Field label="Core values (one per line)">
          <TextArea value={(d.values || []).join("\n")} onChange={(e) => set("values", e.target.value.split("\n").filter(Boolean))} />
        </Field>
      </SectionCard>

      {/* Timeline */}
      <SectionCard
        title="Timeline"
        subtitle="Milestones shown as an alternating timeline. Add / remove rows freely — public page updates automatically."
        action={
          <button onClick={addTimeline} data-testid="add-timeline" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium">
            <Plus className="w-3 h-3" /> Add milestone
          </button>
        }
      >
        <div className="grid gap-3">
          {timeline.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[80px,140px,1fr,auto] gap-2 items-start" data-testid={`timeline-row-${i}`}>
              <Input value={row.year} placeholder="Year" onChange={(e) => updateTimeline(i, "year", e.target.value)} />
              <Input value={row.title} placeholder="Title" onChange={(e) => updateTimeline(i, "title", e.target.value)} />
              <Input value={row.body} placeholder="Description" onChange={(e) => updateTimeline(i, "body", e.target.value)} />
              <button onClick={() => removeTimeline(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {timeline.length === 0 && <div className="text-xs text-gray-400">Using default 4 milestones. Add your own to override.</div>}
        </div>
      </SectionCard>

      {/* Achievements / Stats */}
      <SectionCard
        title="Achievement counters"
        subtitle="Animated stats shown after the timeline. Use numbers with optional suffix like '12,400+', '94%'."
        action={
          <button onClick={addAch} data-testid="add-achievement" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium">
            <Plus className="w-3 h-3" /> Add stat
          </button>
        }
      >
        <div className="grid gap-3">
          {achievements.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px,1fr,auto] gap-2 items-start" data-testid={`achievement-row-${i}`}>
              <Input value={row.k} placeholder="Value (e.g. 12,400+)" onChange={(e) => updateAch(i, "k", e.target.value)} />
              <Input value={row.v} placeholder="Label (e.g. Teachers trained)" onChange={(e) => updateAch(i, "v", e.target.value)} />
              <button onClick={() => removeAch(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {achievements.length === 0 && <div className="text-xs text-gray-400">Using 5 default stats. Add your own to override.</div>}
        </div>
      </SectionCard>

      {/* Why Choose */}
      <SectionCard
        title="Why choose us"
        subtitle="Premium feature grid. Icon names accept: GraduationCap, BookOpen, ShieldCheck, Lightbulb, Zap, Award, Rocket, Sparkles, Users, Heart, Trophy."
        action={
          <button onClick={addWhy} data-testid="add-why" className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-medium">
            <Plus className="w-3 h-3" /> Add reason
          </button>
        }
      >
        <div className="grid gap-3">
          {why.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px,1fr,auto] gap-2 items-start" data-testid={`why-row-${i}`}>
              <Input value={row.icon} placeholder="Icon (e.g. Award)" onChange={(e) => updateWhy(i, "icon", e.target.value)} />
              <div className="grid gap-2">
                <Input value={row.title} placeholder="Title" onChange={(e) => updateWhy(i, "title", e.target.value)} />
                <TextArea value={row.body} placeholder="Body" onChange={(e) => updateWhy(i, "body", e.target.value)} />
              </div>
              <button onClick={() => removeWhy(i)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {why.length === 0 && <div className="text-xs text-gray-400">Using 7 default reasons. Add your own to override.</div>}
        </div>
      </SectionCard>

      <div className="rounded-2xl border border-dashed border-gray-200 p-5 text-xs text-gray-500">
        Members, Testimonials, Partner logos, and Gallery are managed on other tabs
        (<span className="font-medium">Members</span>, <span className="font-medium">Testimonials</span>, <span className="font-medium">Home page → partners</span>, and the Gallery module).
        All of those feed the About page automatically.
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 rounded-lg w-fit"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-semibold text-[#0F172A]">{title}</div>
          {subtitle && <div className="text-xs text-gray-500 mt-0.5 max-w-xl">{subtitle}</div>}
        </div>
        {action}
      </div>
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

function MembersPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/cms/members/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const blank = { name: "", designation: "", department: "", specialization: "", bio: "", photo: "", email: "", phone: "", linkedin: "", website: "", experience: "", order: 100, active: true, featured: false, qualifications: [], subjects: [] };
  const save = async () => {
    const payload = {
      ...editing,
      qualifications: typeof editing.qualifications === "string" ? editing.qualifications.split("\n").filter(Boolean) : editing.qualifications,
      subjects: typeof editing.subjects === "string" ? editing.subjects.split("\n").filter(Boolean) : editing.subjects,
    };
    if (editing.id) await api.put(`/cms/members/${editing.id}`, payload);
    else await api.post("/cms/members", payload);
    setEditing(null); load(); toast.success("Member saved");
  };
  const del = async (id) => { if (!confirm("Delete member?")) return; await api.delete(`/cms/members/${id}`); load(); };
  return (
    <div className="grid gap-4">
      <button onClick={() => setEditing(blank)} data-testid="add-member" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg w-fit"><Plus className="w-4 h-4" /> Add member</button>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m) => (
          <div key={m.id} className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex gap-3">
              {m.photo ? <img src={m.photo.startsWith("http") ? m.photo : `${BACKEND}${m.photo}`} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-gray-100" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-1.5">
                  {m.name}
                  {m.featured && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">Featured</span>}
                </div>
                <div className="text-xs text-gray-500 truncate">{m.designation}</div>
                <div className="text-xs text-gray-400 mt-1">Order: {m.order} · {m.active ? "Active" : "Hidden"}</div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing({ ...m, qualifications: (m.qualifications || []).join("\n"), subjects: (m.subjects || []).join("\n") })} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700">Edit</button>
              <button onClick={() => del(m.id)} className="text-xs px-2.5 py-1 rounded-lg bg-red-50 text-red-700 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-gray-500 text-sm">No members yet.</div>}
      </div>
      {editing && (
        <AdminModal
          onClose={() => setEditing(null)}
          title={editing.id ? "Edit member" : "New member"}
          testId="member-modal"
          footer={<button onClick={save} data-testid="member-save" className="h-11 px-5 rounded-lg bg-[#0F172A] text-white text-sm font-medium w-full sm:w-auto">Save</button>}
        >
          <div className="grid gap-3">
            <ImageUpload value={editing.photo} onChange={(v) => setEditing({ ...editing, photo: v })} testid="member-photo" />
            {editing.photo && (
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Image position</span>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, imagePositionX: 0, imagePositionY: 0, imageScale: 100 })}
                    data-testid="member-position-reset"
                    className="text-xs font-medium text-[#4F46E5] hover:underline"
                  >
                    Reset position
                  </button>
                </div>

                <div className="w-full aspect-square max-w-[160px] mx-auto rounded-full overflow-hidden bg-gray-100 mb-4">
                  <img
                    src={resolveTrainerImage(editing.photo)}
                    alt=""
                    className="w-full h-full object-cover"
                    style={trainerImageStyle(editing)}
                    data-testid="member-position-preview"
                  />
                </div>

                <div className="grid gap-3">
                  <label className="block">
                    <span className="text-xs text-gray-500 flex justify-between"><span>Horizontal position</span><span>{editing.imagePositionX ?? 0}</span></span>
                    <input
                      type="range" min={-100} max={100}
                      value={editing.imagePositionX ?? 0}
                      onChange={(e) => setEditing({ ...editing, imagePositionX: Number(e.target.value) })}
                      data-testid="member-position-x"
                      className="w-full"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500 flex justify-between"><span>Vertical position</span><span>{editing.imagePositionY ?? 0}</span></span>
                    <input
                      type="range" min={-100} max={100}
                      value={editing.imagePositionY ?? 0}
                      onChange={(e) => setEditing({ ...editing, imagePositionY: Number(e.target.value) })}
                      data-testid="member-position-y"
                      className="w-full"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500 flex justify-between"><span>Image scale</span><span>{editing.imageScale ?? 100}%</span></span>
                    <input
                      type="range" min={80} max={150}
                      value={editing.imageScale ?? 100}
                      onChange={(e) => setEditing({ ...editing, imageScale: Number(e.target.value) })}
                      data-testid="member-position-scale"
                      className="w-full"
                    />
                  </label>
                </div>
              </div>
            )}
            {[["name", "Name"], ["designation", "Designation"], ["department", "Department (Organization)"], ["specialization", "Specialization"], ["email", "Email"], ["phone", "Phone"], ["linkedin", "LinkedIn URL"], ["website", "Website URL"], ["experience", "Experience (e.g. 12 yrs)"]].map(([k, l]) => (
              <Input key={k} placeholder={l} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} data-testid={`member-${k}`} />
            ))}
            <TextArea placeholder="Biography" value={editing.bio || ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} />
            <TextArea placeholder="Subjects (one per line, e.g. Physics)" value={editing.subjects || ""} onChange={(e) => setEditing({ ...editing, subjects: e.target.value })} data-testid="member-subjects" />
            <TextArea placeholder="Qualifications & certifications (one per line)" value={editing.qualifications || ""} onChange={(e) => setEditing({ ...editing, qualifications: e.target.value })} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 100 })} />
              <label className="flex items-center gap-2 text-sm px-3"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="member-active" /> Active</label>
              <label className="flex items-center gap-2 text-sm px-3"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="member-featured" /> Featured</label>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/cms/testimonials/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const blank = { quote: "", author: "", role: "", school: "", photo: "", rating: 5, verified: false, featured: false, order: 100, active: true };
  const save = async () => {
    if (editing.id) await api.put(`/cms/testimonials/${editing.id}`, editing);
    else await api.post("/cms/testimonials", editing);
    setEditing(null); load(); toast.success("Testimonial saved");
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/cms/testimonials/${id}`); load(); };
  return (
    <div className="grid gap-4">
      <button onClick={() => setEditing(blank)} data-testid="add-testimonial" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg w-fit"><Plus className="w-4 h-4" /> Add testimonial</button>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="text-sm italic text-gray-700">"{t.quote}"</div>
            <div className="mt-3 text-xs text-gray-500">— {t.author}, {t.role} · {t.school}</div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(t)} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700">Edit</button>
              <button onClick={() => del(t.id)} className="text-xs px-2.5 py-1 rounded-lg bg-red-50 text-red-700">Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-gray-500 text-sm col-span-full">No testimonials yet.</div>}
      </div>
      {editing && (
        <AdminModal
          onClose={() => setEditing(null)}
          title={`${editing.id ? "Edit" : "New"} testimonial`}
          testId="testimonial-modal"
          footer={<button onClick={save} data-testid="testimonial-save" className="h-11 px-5 rounded-lg bg-[#0F172A] text-white text-sm font-medium w-full sm:w-auto">Save</button>}
        >
          <div className="grid gap-3">
            <TextArea placeholder="Quote" value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} data-testid="testimonial-quote" />
            <Input placeholder="Author name" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
            <Input placeholder="Role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} />
            <Input placeholder="School" value={editing.school} onChange={(e) => setEditing({ ...editing, school: e.target.value })} />
            <ImageUpload value={editing.photo} onChange={(v) => setEditing({ ...editing, photo: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input type="number" placeholder="Rating (1-5)" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: parseInt(e.target.value) || 5 })} />
              <Input type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 100 })} />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={!!editing.verified} onChange={(e) => setEditing({ ...editing, verified: e.target.checked })} data-testid="testimonial-verified" /> Verified school
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="testimonial-featured" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="testimonial-active" /> Active
              </label>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

function ContactPanel() {
  const { reload } = useCms();
  const [d, setD] = useEditable("/cms/contact");
  const [saving, setSaving] = useState(false);
  if (!d) return <div className="text-gray-500">Loading…</div>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => {
    setSaving(true);
    try { await api.put("/cms/contact", d); await reload(); toast.success("Contact page saved"); }
    catch { toast.error("Could not save. Please retry."); }
    finally { setSaving(false); }
  };

  // Generic add/update/move/remove for any array field on the contact doc —
  // avoids writing the same three functions once per list (cards, faqs, topics, etc).
  const arrayOf = (key, blank) => {
    const list = Array.isArray(d[key]) ? d[key] : [];
    return {
      list,
      update: (i, patch) => set(key, list.map((row, idx) => (idx === i ? { ...row, ...patch } : row))),
      move: (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        const next = [...list];
        [next[i], next[j]] = [next[j], next[i]];
        set(key, next);
      },
      add: () => set(key, [...list, blank]),
      remove: (i) => set(key, list.filter((_, idx) => idx !== i)),
    };
  };

  const cardsH = arrayOf("cards", { icon: "Sparkles", title: "New card", description: "", link: "", button_text: "", bg_color: "", visible: true });
  const faqsH = arrayOf("faqs", { question: "", answer: "", visible: true });
  const topicsH = arrayOf("topics", { label: "New topic", visible: true });
  const supportH = arrayOf("support_cards", { icon: "Sparkles", title: "New item", description: "", value: "", link: "", button_text: "", visible: true });
  const officesH = arrayOf("offices", { city: "", address: "", contact: "", visible: true });

  const images = Array.isArray(d.office_images) ? d.office_images : [];
  const addImage = (url) => url && set("office_images", [...images, url]);
  const removeImage = (i) => set("office_images", images.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    set("office_images", next);
  };

  return (
    <div className="grid gap-6">
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="contact-cms-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Hero */}
      <CmsSection title="Hero">
        <Field label="Badge text (optional)"><Input value={d.hero_badge || ""} onChange={(e) => set("hero_badge", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title"><Input value={d.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="contact-hero-title" /></Field>
          <Field label="Subtitle"><Input value={d.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} /></Field>
        </div>
        <Field label="Description"><TextArea value={d.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} /></Field>
        <Field label="Background image / GIF"><ImageUpload value={d.hero_media} onChange={(v) => set("hero_media", v)} testid="contact-hero-media" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA button text"><Input value={d.hero_cta_text || ""} onChange={(e) => set("hero_cta_text", e.target.value)} /></Field>
          <Field label="CTA button link"><Input value={d.hero_cta_link || ""} onChange={(e) => set("hero_cta_link", e.target.value)} /></Field>
        </div>
      </CmsSection>

      {/* Contact form section text */}
      <CmsSection title="Contact form section">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Eyebrow"><Input value={d.form_eyebrow || ""} onChange={(e) => set("form_eyebrow", e.target.value)} /></Field>
          <Field label="Section title"><Input value={d.form_title || ""} onChange={(e) => set("form_title", e.target.value)} /></Field>
        </div>
        <Field label="Intro description"><Input value={d.form_intro || ""} onChange={(e) => set("form_intro", e.target.value)} /></Field>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Field labels &amp; placeholders</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["label_first_name", "First name label"], ["placeholder_first_name", "First name placeholder"],
            ["label_last_name", "Last name label"], ["placeholder_last_name", "Last name placeholder"],
            ["label_email", "Email label"], ["placeholder_email", "Email placeholder"],
            ["label_topic", "Topic label"], ["placeholder_topic", "Topic placeholder"],
            ["label_message", "Message label"], ["placeholder_message", "Message placeholder"],
          ].map(([k, ph]) => (
            <Input key={k} placeholder={ph} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} />
          ))}
        </div>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Topics dropdown</div>
        <div className="grid gap-2">
          {topicsH.list.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={t.label} onChange={(e) => topicsH.update(i, { label: e.target.value })} className="flex-1" />
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={t.visible !== false} onChange={(e) => topicsH.update(i, { visible: e.target.checked })} /> Show
              </label>
              <ReorderControls onUp={() => topicsH.move(i, -1)} onDown={() => topicsH.move(i, 1)} onRemove={() => topicsH.remove(i)} upDisabled={i === 0} downDisabled={i === topicsH.list.length - 1} />
            </div>
          ))}
          <button onClick={topicsH.add} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add topic</button>
        </div>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Consent &amp; submit</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Consent text"><Input value={d.consent_text || ""} onChange={(e) => set("consent_text", e.target.value)} /></Field>
          <Field label="Privacy link label"><Input value={d.privacy_text || ""} onChange={(e) => set("privacy_text", e.target.value)} /></Field>
          <Field label="Privacy link URL"><Input value={d.privacy_link || ""} onChange={(e) => set("privacy_link", e.target.value)} /></Field>
          <Field label="Submit button text"><Input value={d.submit_text || ""} onChange={(e) => set("submit_text", e.target.value)} /></Field>
          <Field label="Submit loading text"><Input value={d.submit_loading_text || ""} onChange={(e) => set("submit_loading_text", e.target.value)} /></Field>
          <Field label="Success message"><Input value={d.submit_success_message || ""} onChange={(e) => set("submit_success_message", e.target.value)} /></Field>
          <Field label="Error message"><Input value={d.submit_error_message || ""} onChange={(e) => set("submit_error_message", e.target.value)} /></Field>
        </div>
        <p className="text-xs text-gray-400 mt-1">Form validation, submission, and email delivery are unchanged — this only edits the text around the form.</p>
      </CmsSection>

      {/* Direct support section */}
      <CmsSection title="Direct support section">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section title"><Input value={d.support_title || ""} onChange={(e) => set("support_title", e.target.value)} /></Field>
          <Field label="Subtitle"><Input value={d.support_subtitle || ""} onChange={(e) => set("support_subtitle", e.target.value)} /></Field>
        </div>
        <div className="grid gap-3">
          {supportH.list.map((s, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-4 grid gap-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Input value={s.title} onChange={(e) => supportH.update(i, { title: e.target.value })} placeholder="Title" className="flex-1 min-w-[120px]" />
                <Input value={s.icon} onChange={(e) => supportH.update(i, { icon: e.target.value })} placeholder="Icon (lucide)" className="w-32" />
                <Input value={s.value} onChange={(e) => supportH.update(i, { value: e.target.value })} placeholder="Value" className="flex-1 min-w-[120px]" />
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={s.visible !== false} onChange={(e) => supportH.update(i, { visible: e.target.checked })} /> Show
                </label>
                <ReorderControls onUp={() => supportH.move(i, -1)} onDown={() => supportH.move(i, 1)} onRemove={() => supportH.remove(i)} upDisabled={i === 0} downDisabled={i === supportH.list.length - 1} />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={s.description} onChange={(e) => supportH.update(i, { description: e.target.value })} placeholder="Description (small text above value)" />
                <Input value={s.link} onChange={(e) => supportH.update(i, { link: e.target.value })} placeholder="Link (optional)" />
              </div>
            </div>
          ))}
          <button onClick={supportH.add} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add item</button>
        </div>
      </CmsSection>

      {/* Headquarters */}
      <CmsSection title="Headquarters">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section title"><Input value={d.hq_section_title || ""} onChange={(e) => set("hq_section_title", e.target.value)} /></Field>
          <Field label="Office name (optional)"><Input value={d.hq_office_name || ""} onChange={(e) => set("hq_office_name", e.target.value)} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Address line 1"><Input value={d.office_address || ""} onChange={(e) => set("office_address", e.target.value)} /></Field>
          <Field label="Address line 2"><Input value={d.office_address_2 || ""} onChange={(e) => set("office_address_2", e.target.value)} /></Field>
        </div>
        <Field label="Landmark"><Input value={d.office_landmark || ""} onChange={(e) => set("office_landmark", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="City"><Input value={d.office_city || ""} onChange={(e) => set("office_city", e.target.value)} /></Field>
          <Field label="State"><Input value={d.office_state || ""} onChange={(e) => set("office_state", e.target.value)} /></Field>
          <Field label="PIN code"><Input value={d.office_pin || ""} onChange={(e) => set("office_pin", e.target.value)} /></Field>
          <Field label="Country"><Input value={d.office_country || ""} onChange={(e) => set("office_country", e.target.value)} /></Field>
        </div>
        <Field label="Get directions button text"><Input value={d.hq_directions_text || ""} onChange={(e) => set("hq_directions_text", e.target.value)} className="max-w-xs" /></Field>
      </CmsSection>

      {/* Additional offices */}
      <CmsSection title="Additional offices">
        <div className="grid gap-3">
          {officesH.list.map((o, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-4 grid gap-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Input value={o.city} onChange={(e) => officesH.update(i, { city: e.target.value })} placeholder="City" className="flex-1 min-w-[120px]" />
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={o.visible !== false} onChange={(e) => officesH.update(i, { visible: e.target.checked })} /> Show
                </label>
                <ReorderControls onUp={() => officesH.move(i, -1)} onDown={() => officesH.move(i, 1)} onRemove={() => officesH.remove(i)} upDisabled={i === 0} downDisabled={i === officesH.list.length - 1} />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={o.address} onChange={(e) => officesH.update(i, { address: e.target.value })} placeholder="Address" />
                <Input value={o.contact} onChange={(e) => officesH.update(i, { contact: e.target.value })} placeholder="Contact (phone or email)" />
              </div>
            </div>
          ))}
          <button onClick={officesH.add} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add office</button>
        </div>
      </CmsSection>

      {/* Contact details */}
      <CmsSection title="Contact details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary phone"><Input value={d.phone_primary || ""} onChange={(e) => set("phone_primary", e.target.value)} /></Field>
          <Field label="Secondary phone"><Input value={d.phone_secondary || ""} onChange={(e) => set("phone_secondary", e.target.value)} /></Field>
          <Field label="WhatsApp"><Input value={d.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
          <Field label="Primary email"><Input value={d.email_primary || ""} onChange={(e) => set("email_primary", e.target.value)} /></Field>
          <Field label="Support email"><Input value={d.email_support || ""} onChange={(e) => set("email_support", e.target.value)} /></Field>
        </div>
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">Working hours</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Monday–Friday"><Input value={d.hours_weekday || ""} onChange={(e) => set("hours_weekday", e.target.value)} /></Field>
          <Field label="Saturday"><Input value={d.hours_saturday || ""} onChange={(e) => set("hours_saturday", e.target.value)} /></Field>
          <Field label="Sunday"><Input value={d.hours_sunday || ""} onChange={(e) => set("hours_sunday", e.target.value)} /></Field>
        </div>
        <p className="text-xs text-gray-400 mt-1">Social links live under Site &amp; Header — same accounts everywhere on the site, not duplicated here.</p>
      </CmsSection>

      {/* Floating Widgets — WhatsApp position/style + Back to Top, unified stacking */}
      <CmsSection title="Floating Widgets">
        <p className="text-xs text-gray-400 -mt-1">
          WhatsApp, the Prospectus button (configured separately under Content → Floating Prospectus), and Back to Top
          all share one stacking system — they never overlap. Lower "Stack Order" sits closer to the page edge.
        </p>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">WhatsApp Button</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Position">
            <select value={d.whatsapp_position || "left-bottom"} onChange={(e) => set("whatsapp_position", e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
              <option value="left-bottom">Left Bottom</option>
              <option value="left-center">Left Center</option>
              <option value="right-bottom">Right Bottom</option>
              <option value="right-center">Right Center</option>
            </select>
          </Field>
          <Field label="Stack Order">
            <Input type="number" value={d.whatsapp_order ?? 2} onChange={(e) => set("whatsapp_order", parseInt(e.target.value) || 0)} />
          </Field>
          <Field label="Distance from Bottom (px)">
            <Input type="number" value={d.whatsapp_distance_bottom ?? 24} onChange={(e) => set("whatsapp_distance_bottom", parseInt(e.target.value) || 0)} />
          </Field>
          <Field label="Distance from Side (px)">
            <Input type="number" value={d.whatsapp_distance_side ?? 24} onChange={(e) => set("whatsapp_distance_side", parseInt(e.target.value) || 0)} />
          </Field>
          <Field label="Button Size (px)">
            <Input type="number" value={d.whatsapp_button_size ?? 56} onChange={(e) => set("whatsapp_button_size", parseInt(e.target.value) || 56)} />
          </Field>
          <Field label="Icon Size (px)">
            <Input type="number" value={d.whatsapp_icon_size ?? 22} onChange={(e) => set("whatsapp_icon_size", parseInt(e.target.value) || 22)} />
          </Field>
          <Field label="Border Radius (px)">
            <Input type="number" value={d.whatsapp_border_radius ?? 9999} onChange={(e) => set("whatsapp_border_radius", parseInt(e.target.value) || 0)} />
          </Field>
          <Field label="Animation">
            <select value={d.whatsapp_animation || "none"} onChange={(e) => set("whatsapp_animation", e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
              <option value="none">None</option>
              <option value="pulse">Pulse</option>
              <option value="bounce">Bounce</option>
              <option value="float">Float</option>
              <option value="glow">Glow</option>
              <option value="slide">Slide</option>
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={d.whatsapp_shadow !== false} onChange={(e) => set("whatsapp_shadow", e.target.checked)} /> Shadow
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={d.whatsapp_desktop_visible !== false} onChange={(e) => set("whatsapp_desktop_visible", e.target.checked)} /> Show on Desktop
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={d.whatsapp_tablet_visible !== false} onChange={(e) => set("whatsapp_tablet_visible", e.target.checked)} /> Show on Tablet
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={d.whatsapp_mobile_visible !== false} onChange={(e) => set("whatsapp_mobile_visible", e.target.checked)} /> Show on Mobile
          </label>
        </div>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-4">Back to Top Button</div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={d.back_to_top_enabled !== false} onChange={(e) => set("back_to_top_enabled", e.target.checked)} /> Enable Back to Top
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Position">
            <select value={d.back_to_top_position || "right-bottom"} onChange={(e) => set("back_to_top_position", e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
              <option value="left-bottom">Left Bottom</option>
              <option value="left-center">Left Center</option>
              <option value="right-bottom">Right Bottom</option>
              <option value="right-center">Right Center</option>
            </select>
          </Field>
          <Field label="Stack Order">
            <Input type="number" value={d.back_to_top_order ?? 1} onChange={(e) => set("back_to_top_order", parseInt(e.target.value) || 0)} />
          </Field>
          <Field label="Button Size (px)">
            <Input type="number" value={d.back_to_top_size ?? 44} onChange={(e) => set("back_to_top_size", parseInt(e.target.value) || 44)} />
          </Field>
          <Field label="Animation">
            <select value={d.back_to_top_animation || "none"} onChange={(e) => set("back_to_top_animation", e.target.value)}
              className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
              <option value="none">None</option>
              <option value="pulse">Pulse</option>
              <option value="bounce">Bounce</option>
              <option value="float">Float</option>
              <option value="glow">Glow</option>
              <option value="slide">Slide</option>
            </select>
          </Field>
        </div>

        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-4">Global Spacing</div>
        <Field label="Vertical Gap Between Stacked Buttons (px)">
          <Input type="number" value={d.floating_widgets_gap ?? 12} onChange={(e) => set("floating_widgets_gap", parseInt(e.target.value) || 12)} />
        </Field>
      </CmsSection>

      {/* Map */}
      <CmsSection title="Google Map">
        <Field label="Embed URL"><Input value={d.map_embed_url || ""} onChange={(e) => set("map_embed_url", e.target.value)} placeholder="https://www.google.com/maps/embed?..." /></Field>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Latitude (optional)"><Input value={d.map_lat || ""} onChange={(e) => set("map_lat", e.target.value)} /></Field>
          <Field label="Longitude (optional)"><Input value={d.map_lng || ""} onChange={(e) => set("map_lng", e.target.value)} /></Field>
          <Field label="Zoom (optional)"><Input value={d.map_zoom || ""} onChange={(e) => set("map_zoom", e.target.value)} /></Field>
          <Field label="Marker title (optional)"><Input value={d.map_marker_title || ""} onChange={(e) => set("map_marker_title", e.target.value)} /></Field>
        </div>
      </CmsSection>

      {/* Information cards */}
      <CmsSection title="Information cards">
        <div className="grid gap-3">
          {cardsH.list.map((c, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-4 grid gap-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Input value={c.title} onChange={(e) => cardsH.update(i, { title: e.target.value })} placeholder="Title" className="flex-1 min-w-[140px]" />
                <Input value={c.icon} onChange={(e) => cardsH.update(i, { icon: e.target.value })} placeholder="Icon name (lucide)" className="w-40" />
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={c.visible !== false} onChange={(e) => cardsH.update(i, { visible: e.target.checked })} /> Show
                </label>
                <ReorderControls onUp={() => cardsH.move(i, -1)} onDown={() => cardsH.move(i, 1)} onRemove={() => cardsH.remove(i)} upDisabled={i === 0} downDisabled={i === cardsH.list.length - 1} />
              </div>
              <TextArea value={c.description} onChange={(e) => cardsH.update(i, { description: e.target.value })} placeholder="Description" />
              <div className="grid sm:grid-cols-3 gap-2">
                <Input value={c.link} onChange={(e) => cardsH.update(i, { link: e.target.value })} placeholder="Link (mailto:, tel:, or URL)" />
                <Input value={c.button_text} onChange={(e) => cardsH.update(i, { button_text: e.target.value })} placeholder="Button text" />
                <input type="color" value={c.bg_color || "#EEF2FF"} onChange={(e) => cardsH.update(i, { bg_color: e.target.value })} className="w-full h-[42px] rounded-lg border border-gray-200 cursor-pointer" title="Background color (optional)" />
              </div>
            </div>
          ))}
          <button onClick={cardsH.add} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add card</button>
        </div>
      </CmsSection>

      {/* Quick answers */}
      <CmsSection title="Quick answers section">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section title"><Input value={d.quick_answers_title || ""} onChange={(e) => set("quick_answers_title", e.target.value)} /></Field>
          <Field label="Subtitle"><Input value={d.quick_answers_subtitle || ""} onChange={(e) => set("quick_answers_subtitle", e.target.value)} /></Field>
        </div>
        <Field label="Description"><TextArea value={d.quick_answers_description || ""} onChange={(e) => set("quick_answers_description", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Button text"><Input value={d.quick_answers_button_text || ""} onChange={(e) => set("quick_answers_button_text", e.target.value)} /></Field>
          <Field label="Button link"><Input value={d.quick_answers_link || ""} onChange={(e) => set("quick_answers_link", e.target.value)} /></Field>
        </div>
      </CmsSection>

      {/* FAQ */}
      <CmsSection title="FAQ">
        <div className="grid gap-3">
          {faqsH.list.map((f, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-4 grid gap-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Input value={f.question} onChange={(e) => faqsH.update(i, { question: e.target.value })} placeholder="Question" className="flex-1 min-w-[160px]" />
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={f.visible !== false} onChange={(e) => faqsH.update(i, { visible: e.target.checked })} /> Show
                </label>
                <ReorderControls onUp={() => faqsH.move(i, -1)} onDown={() => faqsH.move(i, 1)} onRemove={() => faqsH.remove(i)} upDisabled={i === 0} downDisabled={i === faqsH.list.length - 1} />
              </div>
              <TextArea value={f.answer} onChange={(e) => faqsH.update(i, { answer: e.target.value })} placeholder="Answer" />
            </div>
          ))}
          <button onClick={faqsH.add} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add FAQ</button>
        </div>
      </CmsSection>

      {/* Office images */}
      <CmsSection title="Office &amp; team images">
        <div className="flex flex-wrap gap-3">
          {images.map((url, i) => (
            <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
              <img src={url.startsWith("http") ? url : `${BACKEND}${url}`} alt="" className="w-full h-full object-cover" />
              <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center text-gray-600 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>
              <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => moveImage(i, -1)} disabled={i === 0} className="w-6 h-6 rounded bg-white/90 grid place-items-center disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} className="w-6 h-6 rounded bg-white/90 grid place-items-center disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3"><ImageUpload value="" onChange={addImage} testid="contact-office-image-upload" /></div>
        <p className="text-xs text-gray-400 mt-1">Each upload adds a new photo — images and GIFs both supported. Uploading a new one doesn't replace existing photos; delete the old one first if you want to swap it.</p>
      </CmsSection>

      {/* SEO */}
      <CmsSection title="SEO">
        <Field label="Page title"><Input value={d.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></Field>
        <Field label="Meta description"><TextArea value={d.seo_meta_description || ""} onChange={(e) => set("seo_meta_description", e.target.value)} /></Field>
        <Field label="Meta keywords (comma separated)"><Input value={d.seo_meta_keywords || ""} onChange={(e) => set("seo_meta_keywords", e.target.value)} /></Field>
        <Field label="Open Graph image"><ImageUpload value={d.seo_og_image} onChange={(v) => set("seo_og_image", v)} testid="contact-og-image" /></Field>
      </CmsSection>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const CmsSection = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4 max-w-4xl">
    <div className="text-sm font-semibold text-[#1A202C]">{title}</div>
    {children}
  </div>
);

const ReorderControls = ({ onUp, onDown, onRemove, upDisabled, downDisabled }) => (
  <>
    <button onClick={onUp} disabled={upDisabled} className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
    <button onClick={onDown} disabled={downDisabled} className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
    <button onClick={onRemove} className="w-8 h-8 grid place-items-center rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// HOMEPAGE TYPOGRAPHY PANEL
// Stores settings under home.home_typography.{key} via PUT /cms/home
// Home.jsx reads them via home?.home_typography?.[key] with graceful fallback
// ─────────────────────────────────────────────────────────────────────────────

// Default typography values — used for reset and first-paint fallback
const TYPO_DEFAULTS = {
  hero_title:         { fontFamily: "Playfair Display", fontSize: 56, fontWeight: 700, fontStyle: "normal", letterSpacing: -0.02, lineHeight: 1.05, textTransform: "none", color: "", textAlign: "left" },
  hero_subtitle:      { fontFamily: "Inter", fontSize: 18, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "#4B5563", textAlign: "left" },
  hero_eyebrow:       { fontFamily: "Inter", fontSize: 12, fontWeight: 500, fontStyle: "normal", letterSpacing: 0.04, lineHeight: 1.5, textTransform: "none", color: "", textAlign: "left" },
  hero_cta_primary:   { fontFamily: "Inter", fontSize: 14, fontWeight: 600, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.5, textTransform: "none", color: "#FFFFFF", textAlign: "center" },
  hero_cta_secondary: { fontFamily: "Inter", fontSize: 14, fontWeight: 600, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.5, textTransform: "none", color: "", textAlign: "center" },
  stat_number:        { fontFamily: "Inter", fontSize: 24, fontWeight: 600, fontStyle: "normal", letterSpacing: -0.02, lineHeight: 1.2, textTransform: "none", color: "#FFFFFF", textAlign: "center" },
  stat_label:         { fontFamily: "Inter", fontSize: 11, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.4, textTransform: "none", color: "#FFFFFF", textAlign: "center" },
  section_heading:    { fontFamily: "Playfair Display", fontSize: 40, fontWeight: 700, fontStyle: "normal", letterSpacing: -0.01, lineHeight: 1.15, textTransform: "none", color: "#1A202C", textAlign: "center" },
  section_subtitle:   { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "#64748B", textAlign: "center" },
  why_card_title:     { fontFamily: "Inter", fontSize: 18, fontWeight: 600, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.3, textTransform: "none", color: "#1A202C", textAlign: "left" },
  why_card_desc:      { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.5, textTransform: "none", color: "#64748B", textAlign: "left" },
  program_title:      { fontFamily: "Inter", fontSize: 20, fontWeight: 600, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.3, textTransform: "none", color: "", textAlign: "left" },
  program_desc:       { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "", textAlign: "left" },
  journey_step_title: { fontFamily: "Inter", fontSize: 18, fontWeight: 700, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.3, textTransform: "none", color: "#1A202C", textAlign: "center" },
  journey_step_desc:  { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "#64748B", textAlign: "center" },
  testimonial_quote:  { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "#0F172A", textAlign: "left" },
  testimonial_author: { fontFamily: "Inter", fontSize: 15, fontWeight: 600, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.4, textTransform: "none", color: "#1A202C", textAlign: "left" },
  testimonial_role:   { fontFamily: "Inter", fontSize: 14, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.4, textTransform: "none", color: "#64748B", textAlign: "left" },
  partner_name:       { fontFamily: "Inter", fontSize: 14, fontWeight: 500, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.4, textTransform: "none", color: "", textAlign: "center" },
  verify_heading:     { fontFamily: "Playfair Display", fontSize: 36, fontWeight: 700, fontStyle: "normal", letterSpacing: -0.01, lineHeight: 1.15, textTransform: "none", color: "#1A202C", textAlign: "left" },
  verify_desc:        { fontFamily: "Inter", fontSize: 15, fontWeight: 400, fontStyle: "normal", letterSpacing: 0, lineHeight: 1.6, textTransform: "none", color: "#64748B", textAlign: "left" },
  cta_heading:        { fontFamily: "Playfair Display", fontSize: 44, fontWeight: 700, fontStyle: "normal", letterSpacing: -0.01, lineHeight: 1.1, textTransform: "none", color: "#FFFFFF", textAlign: "center" },
};

// Single typography field editor — font family, size, weight, style, color, spacing, etc.
function TypoEditor({ label, value, onChange, defaultVal }) {
  const t = { ...defaultVal, ...value };
  const set = (k, v) => onChange({ ...t, [k]: v });
  const [open, setOpen] = useState(false);

  const preview = (
    <span style={{
      fontFamily: fontCss(t.fontFamily || "Inter"),
      fontSize: Math.min(t.fontSize || 16, 18),
      fontWeight: t.fontWeight || 400,
      fontStyle: t.fontStyle || "normal",
      color: t.color || "#0F172A",
      letterSpacing: t.letterSpacing ? `${t.letterSpacing}em` : undefined,
      textTransform: t.textTransform || "none",
    }}>
      {label}
    </span>
  );

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Type className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          {preview}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange({}); }}
            className="text-[10px] text-gray-400 hover:text-rose-600 px-1.5 py-0.5 rounded border border-gray-200 hover:border-rose-200"
            title="Reset to default"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 py-4 grid gap-3 bg-white">
          {/* Row 1: font family + size + weight */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Font family</label>
              <select
                value={t.fontFamily || "Inter"}
                onChange={(e) => set("fontFamily", e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white"
              >
                {FONT_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.fonts.map((f) => (
                      <option key={f} value={f} style={{ fontFamily: fontCss(f) }}>{f}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Size (px)</label>
              <input type="number" min={8} max={120} value={t.fontSize || 16}
                onChange={(e) => set("fontSize", Number(e.target.value))}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5]" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Weight</label>
              <select value={t.fontWeight || 400}
                onChange={(e) => set("fontWeight", Number(e.target.value))}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white">
                {[300, 400, 500, 600, 700, 800].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: style, transform, align, color */}
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Style</label>
              <select value={t.fontStyle || "normal"}
                onChange={(e) => set("fontStyle", e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white">
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Transform</label>
              <select value={t.textTransform || "none"}
                onChange={(e) => set("textTransform", e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white">
                <option value="none">None</option>
                <option value="uppercase">UPPER</option>
                <option value="lowercase">lower</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Alignment</label>
              <select value={t.textAlign || "left"}
                onChange={(e) => set("textAlign", e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
                <option value="justify">Justify</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Color</label>
              <input type="color" value={t.color || "#0F172A"}
                onChange={(e) => set("color", e.target.value)}
                className="w-full h-[38px] rounded-lg border border-gray-200 cursor-pointer p-0.5" />
            </div>
          </div>

          {/* Row 3: letter spacing, line height, text-decoration */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Letter spacing (em)</label>
              <input type="number" step="0.01" min="-0.1" max="0.5" value={t.letterSpacing ?? 0}
                onChange={(e) => set("letterSpacing", Number(e.target.value))}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5]" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Line height</label>
              <input type="number" step="0.05" min="1" max="3" value={t.lineHeight ?? 1.5}
                onChange={(e) => set("lineHeight", Number(e.target.value))}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5]" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 block mb-1">Decoration</label>
              <select value={t.textDecoration || "none"}
                onChange={(e) => set("textDecoration", e.target.value)}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-[#4F46E5] bg-white">
                <option value="none">None</option>
                <option value="underline">Underline</option>
                <option value="line-through">Strikethrough</option>
              </select>
            </div>
          </div>

          {/* Live preview strip */}
          <div className="mt-1 px-3 py-3 rounded-lg bg-gray-50 border border-gray-100">
            <span style={{
              fontFamily: fontCss(t.fontFamily || "Inter"),
              fontSize: Math.min(t.fontSize || 16, 24),
              fontWeight: t.fontWeight || 400,
              fontStyle: t.fontStyle || "normal",
              letterSpacing: t.letterSpacing ? `${t.letterSpacing}em` : undefined,
              lineHeight: t.lineHeight || 1.5,
              textTransform: t.textTransform || "none",
              textDecoration: t.textDecoration && t.textDecoration !== "none" ? t.textDecoration : undefined,
              textAlign: t.textAlign || "left",
              color: t.color || "#0F172A",
              display: "block",
            }}>
              The quick brown fox jumps over the lazy dog — {label}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Collapsible section wrapper for the Typography panel
function TypoSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-[#0F172A]">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 bg-white border-t border-gray-100 grid gap-3">
          {children}
        </div>
      )}
    </div>
  );
}

function TypographyPanel() {
  const { reload } = useCms();
  const [d, setD] = useEditable("/cms/home");
  const [saving, setSaving] = useState(false);
  if (!d) return <div className="text-gray-500">Loading…</div>;

  const typo = d.home_typography || {};
  const setTypo = (key, val) => setD({ ...d, home_typography: { ...typo, [key]: val } });
  const resetAll = () => {
    if (!window.confirm("Reset all homepage typography to defaults?")) return;
    setD({ ...d, home_typography: {} });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/cms/home", d); await reload(); toast.success("Typography saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const te = (key) => (
    <TypoEditor
      key={key}
      label={key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
      value={typo[key] || {}}
      onChange={(val) => setTypo(key, val)}
      defaultVal={TYPO_DEFAULTS[key] || {}}
    />
  );

  return (
    <div className="max-w-3xl grid gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Click any element to expand its typography controls. Changes save with the button below.</p>
        <button onClick={resetAll} className="inline-flex items-center gap-1.5 text-xs text-rose-600 border border-rose-200 px-3 py-1.5 rounded-lg hover:bg-rose-50">
          <RotateCcw className="w-3 h-3" /> Reset all
        </button>
      </div>

      <TypoSection title="Hero Section" defaultOpen={true}>
        {te("hero_eyebrow")}
        {te("hero_title")}
        {te("hero_subtitle")}
        {te("hero_cta_primary")}
        {te("hero_cta_secondary")}
        {te("stat_number")}
        {te("stat_label")}
      </TypoSection>

      <TypoSection title="Section Headings &amp; Subtitles (global defaults)">
        {te("section_heading")}
        {te("section_subtitle")}
      </TypoSection>

      <TypoSection title="Why Choose Us">
        {te("why_card_title")}
        {te("why_card_desc")}
      </TypoSection>

      <TypoSection title="Featured Programs">
        {te("program_title")}
        {te("program_desc")}
      </TypoSection>

      <TypoSection title="Training Journey">
        {te("journey_step_title")}
        {te("journey_step_desc")}
      </TypoSection>

      <TypoSection title="Testimonials">
        {te("testimonial_quote")}
        {te("testimonial_author")}
        {te("testimonial_role")}
      </TypoSection>

      <TypoSection title="Partner Schools">
        {te("partner_name")}
      </TypoSection>

      <TypoSection title="Certificate Verification CTA">
        {te("verify_heading")}
        {te("verify_desc")}
      </TypoSection>

      <TypoSection title="Bottom CTA Section">
        {te("cta_heading")}
      </TypoSection>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-5 py-2.5 rounded-lg disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save typography"}
        </button>
      </div>
    </div>
  );
}
